import {
  RMD_TABLE, SINGLE_LIFE_TABLE, FEDERAL_TAX_BRACKETS, CAPITAL_GAINS_BRACKETS, NIIT_THRESHOLD,
  STANDARD_DEDUCTION, MEDICARE_IRMAA, TAX_CONSTANTS_YEAR, ACA, CONTRIBUTION_LIMITS, SENIOR_DEDUCTION,
} from '../constants/taxConstants';
import { taxDeferred } from './payTax';
import { calculateSocialSecurityTax } from './taxEngine';
import { benchmarkPremium, CHILD_COVER_END_AGE } from './healthCover';
import { adjustBenefitForClaimingAge, getFullRetirementAge, SS_WAGE_BASE, spousalBenefit, survivorBase, survivorFactor } from './socialSecurity';
import { WITHDRAWAL_STRATEGY_LABELS, conversionParts, creditCode, phaseCode } from './withdrawalStrategies';
import { DEFAULT_STRATEGY_SETTINGS } from '../../config/planDefaults';
import { buildSavingsPlan } from './savingsPlan';
import { RISK_PROFILES, generateCorrelatedSequences, hashSeed } from '../monteCarloSimulation';
import { HISTORICAL_RETURNS, HISTORY_FIRST_YEAR } from '../constants/historicalReturns';
import { STATE_TAX_RATES } from '../constants/stateTaxRates';
import { stateIncomeTax, stateBracketsFixed, localIncomeTaxOf } from './stateTax';

// ── Stated modelling assumptions (shown to the user alongside results) ──────
// A plan "succeeds" on a simulated path when after-tax spending never falls
// below this fraction of the starting level and savings never run out while a
// year still needs them. One test for every strategy.
export const SPENDING_FLOOR = 0.8;
// A year's draw may fall short of the balance by this share of the draw (rounding
// at the very end of a plan) before savings count as having run out.
const RAN_OUT_TOLERANCE = 0.01;
export const SUCCESS_TARGET = 0.95;
// Share of today's taxable balance taken to be gain when no cost basis is entered.
export const DEFAULT_GAINS_FRACTION = 0.5;
// The 10% additional tax on traditional withdrawals before age 59 1/2. Ages are
// whole years here, so it applies through the year someone is 59.
export const EARLY_WITHDRAWAL_PENALTY = 0.10;
export const PENALTY_FREE_AGE = 60;
// 72(t) payments use the fixed amortisation method at the highest interest
// rate the IRS allows regardless of market rates (Notice 2022-6), and run for
// five years or until the penalty-free age, whichever is later.
export const SEPP_INTEREST_RATE = 0.05;
// What selling a property costs, as a share of the price, unless the person says otherwise:
// agents, transfer costs and tax on the gain together.
export const PROPERTY_SELL_COST = 0.08;
// `onPlan` makes a year's conversion only when the portfolio is at least this
// share of what the plan's projection has at that age. `catchUp` does the same
// and, in a later year that is on plan, also makes up skipped conversions, as
// much as one more year's worth, in years whose limit is a tax bracket.
export const CONVERSION_PACES = ['steady', 'onPlan', 'catchUp'];
export const pacedConversions = (pace) => pace === 'onPlan' || pace === 'catchUp';
// How far below a limit with a cliff (the health credit, a Medicare surcharge tier) a conversion stops.
export const CONVERSION_LIMIT_MARGIN = 500;
export const CONVERSION_PACE_FLOOR = 0.9;
// Medicare's income-related premium looks at the tax return from this many years before.
export const IRMAA_LOOKBACK_YEARS = 2;
export const SEPP_MIN_YEARS = 5;
// 'fit' sizes the payment to what the plan would otherwise take with a penalty.
export const SEPP_MODES = ['fit', 'max', 'custom'];
// `creditAware` is taxable first, except that in a year a premium credit is in
// reach it stops selling taxable holdings where their gains would take income
// over the credit limit, and uses Roth money that comes out free instead.
export const WITHDRAWAL_ORDERS = ['proportional', 'taxableFirst', 'creditAware'];
export const CONVERSION_RATES = [0.10, 0.12, 0.22, 0.24];
// A conversion "ladder": each year convert what the plan will need five years on.
export const LADDER = 'ladder';
// Converted money can be taken out without the penalty once it is this old.
export const CONVERSION_SEASONING_YEARS = 5;
// Leaving an employer in or after the year you turn this age frees that
// employer's 401(k) from the early-withdrawal penalty (the "rule of 55").
export const RULE_OF_55_AGE = 55;
// Share of a Roth balance assumed to be contributions when it is not entered.
export const DEFAULT_ROTH_BASIS_FRACTION = 0.5;
// Health insurance bought privately is needed until Medicare starts.
export const MEDICARE_AGE = 65;
export const MAX_STATE_TAX_RATE = 0.15;
// The highest county income tax rate accepted (Maryland's top county rate is 3.3%).
export const MAX_LOCAL_TAX_RATE = 0.05;

// Yearly cost of investing (fund charges, advice), taken off every year's return.
export const MAX_FEE = 0.03;
// A view on future returns: the long-run average, or a point a year either side.
export const OUTLOOK_SHIFTS = { lower: -0.01, average: 0, higher: 0.01 };
// Social Security and Medicare tax on pay earned in retirement: the Social
// Security part stops at each earner's wage base, the Medicare part does not.
export const PAYROLL_TAX_RATE = 0.0765;
const MEDICARE_PAYROLL_RATE = 0.0145;
// A widow or widower can take the other's benefit from this age.
export const SURVIVOR_BENEFIT_AGE = 60;
export const MAX_LIST_ROWS = 8;
export const OTHER_INCOME_KINDS = ['work', 'taxable', 'taxFree'];
// Rate used to value what is left in traditional accounts at the end of the plan.
export const TRADITIONAL_EXIT_TAX = 0.22;
export const DEFAULT_NUM_SIMS = 2000;

export const STRATEGY_KEYS = ['fixed', 'guardrails', 'bucket', 'percent', 'vanguard', 'vpw', 'rmd'];
// Strategies that hold a spending level, so "the highest level that holds
// up" means something. The others set spending from the balance each year;
// any starting level fails the floor test as soon as the market dips.
const SEARCHABLE = ['fixed', 'guardrails', 'bucket'];
const LABELS = {
  fixed: WITHDRAWAL_STRATEGY_LABELS.fixed,
  guardrails: WITHDRAWAL_STRATEGY_LABELS.guyton_klinger,
  bucket: WITHDRAWAL_STRATEGY_LABELS.bucket,
  percent: WITHDRAWAL_STRATEGY_LABELS.percent,
  vanguard: WITHDRAWAL_STRATEGY_LABELS.vanguard_dynamic,
  vpw: WITHDRAWAL_STRATEGY_LABELS.vpw,
  rmd: WITHDRAWAL_STRATEGY_LABELS.rmd_based,
};

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const dollars = (v) => Math.max(0, Number(v) || 0);
const wholeAge = (v, fallback) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && n > 0 ? n : fallback;
};
const isMarriedStatus = (status) => status === 'married_filing_jointly' || status === 'married_filing_separately';

// ── Inputs ──────────────────────────────────────────────────────────────────

const RETURN_BY_SHARE = Object.values(RISK_PROFILES)
  .map((profile) => [profile.stocks, profile.meanReturn / 100])
  .sort((a, b) => a[0] - b[0]);

/** Expected real return for a share held in stocks, read off the four measured mixes. */
export function expectedReturnForShare(share) {
  const points = RETURN_BY_SHARE;
  if (share <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i++) {
    if (share <= points[i][0]) {
      const [x0, y0] = points[i - 1];
      const [x1, y1] = points[i];
      return y0 + (y1 - y0) * (share - x0) / (x1 - x0);
    }
  }
  return points[points.length - 1][1];
}

/** Expected return in a given year from now: it moves when the mix does. */
export const returnAt = (inputs, yearsFromNow) => (inputs.expectedReturns
  ? inputs.expectedReturns[Math.min(Math.max(0, yearsFromNow), inputs.expectedReturns.length - 1)]
  : inputs.expectedReturn);

// A conversion setting as stored in the profile: a bracket rate, or a credit
// policy with or without a bracket rate. Anything else is no conversion.
function requestedConversionOf(value) {
  if (value == null || value === '') return null;
  const parts = conversionParts(typeof value === 'string' && /^(credit|phase)/.test(value) ? value : Number(value));
  if (parts.credit) return parts.rate == null ? creditCode() : CONVERSION_RATES.includes(parts.rate) ? creditCode(parts.rate) : null;
  if (parts.phased) {
    const lateOk = parts.lateRate == null || (CONVERSION_RATES.includes(parts.lateRate) && parts.lateRate < parts.rate);
    return CONVERSION_RATES.includes(parts.rate) && lateOk ? phaseCode(parts.rate, parts.lateRate) : null;
  }
  return CONVERSION_RATES.includes(parts.rate) ? parts.rate : null;
}

// Ages of children to keep on the family's health plan, however they were typed.
export const childAgesOf = (value) => (Array.isArray(value) ? value : String(value ?? '').split(/[^0-9.]+/))
  .filter((age) => String(age).trim() !== '')
  .map((age) => Math.floor(Number(age)))
  .filter((age) => Number.isFinite(age) && age >= 0 && age < CHILD_COVER_END_AGE)
  .slice(0, MAX_CHILDREN);
const MAX_CHILDREN = 8;

const listRows = (list) => (Array.isArray(list) ? list : []).filter((row) => row && typeof row === 'object').slice(0, MAX_LIST_ROWS);

/**
 * Employer 401(k) match in the first year anything is saved, from the same
 * schedule the plan uses (`buildSavingsPlan`), so the two always agree.
 */
export function employerMatch(profile) {
  const currentAge = wholeAge(profile.spouse1?.currentAge, 40);
  const rows = buildSavingsPlan(profile, { currentAge, retirementAge: Math.max(currentAge + 1, wholeAge(profile.spouse1?.retirementAge, 65)) });
  const first = rows.find((row) => row.total > 0);
  return first ? first.match : 0;
}

/** Shares of new savings as entered ({ traditional, roth, taxable } in percent), scaled to add up to one. Null when none are chosen. */
export function savingsSplitOf(split) {
  if (!split) return null;
  const part = (key) => Math.max(0, Number(split[key]) || 0);
  const sum = part('traditional') + part('roth') + part('taxable');
  if (!(sum > 0)) return null;
  return { traditional: part('traditional') / sum, roth: part('roth') / sum, taxable: part('taxable') / sum };
}

/**
 * Normalises the saved profile into the engine's inputs. Every field the
 * results depend on is in here, so `key` identifies exactly what a result was
 * computed from (used for the stale-results check and the simulation seed).
 */
export function buildPlanInputs(profile, simulationConfig = {}, currentYear = new Date().getFullYear()) {
  const filingStatus = FEDERAL_TAX_BRACKETS[profile.filingStatus] ? profile.filingStatus : 'married_filing_jointly';
  const married = isMarriedStatus(filingStatus);
  const currentAge = clamp(wholeAge(profile.spouse1?.currentAge, 40), 10, 110);
  const startAge = Math.max(currentAge, clamp(wholeAge(profile.spouse1?.retirementAge, 65), 10, 110));
  const lifeAge = clamp(wholeAge(profile.lifeExpectancy, 90), startAge + 1, 120);
  const spouseAge = married ? clamp(wholeAge(profile.spouse2?.currentAge, currentAge), 18, 110) : null;
  const birthYear = currentYear - currentAge;
  const fra = Math.round(getFullRetirementAge(birthYear));
  const riskProfile = RISK_PROFILES[simulationConfig.riskProfile] ? simulationConfig.riskProfile : 'balanced';

  const acct = profile.accounts || {};
  const traditional = dollars(acct.trad401k) + dollars(acct.tradIRA);
  const roth = dollars(acct.roth401k) + dollars(acct.rothIRA) + dollars(acct.hsa);
  const taxable = dollars(acct.taxable);
  const total = traditional + roth + taxable;

  // A blank cost basis means "unknown", which is different from a basis of zero.
  const basisEntered = profile.taxableCostBasis != null && profile.taxableCostBasis !== ''
    && Number.isFinite(Number(profile.taxableCostBasis));
  const taxableBasis = basisEntered
    ? Math.min(taxable, dollars(profile.taxableCostBasis))
    : taxable * (1 - DEFAULT_GAINS_FRACTION);

  // What was paid into the Roth accounts. It can come out at any age without
  // tax or penalty; the growth on top of it cannot until 59 1/2.
  const rothBasisEntered = profile.rothContributions != null && profile.rothContributions !== ''
    && Number.isFinite(Number(profile.rothContributions));
  const rothBasis = rothBasisEntered
    ? Math.min(roth, dollars(profile.rothContributions))
    : roth * DEFAULT_ROTH_BASIS_FRACTION;
  const ruleOf55 = !!profile.ruleOf55 && startAge >= RULE_OF_55_AGE && startAge < PENALTY_FREE_AGE && dollars(acct.trad401k) > 0;
  const requestedConversion = profile.rothConversionRate === LADDER
    // A ladder only helps if the converted money seasons before the penalty ends anyway.
    ? (startAge + CONVERSION_SEASONING_YEARS < PENALTY_FREE_AGE && traditional > 0 ? LADDER : null)
    : requestedConversionOf(profile.rothConversionRate);

  const setting = (key, lo, hi) => {
    const value = profile.strategySettings?.[key];
    const n = Number(value);
    return clamp(value != null && value !== '' && Number.isFinite(n) ? n : DEFAULT_STRATEGY_SETTINGS[key], lo, hi);
  };
  // 72(t) payments only mean something for a retirement that starts before
  // the penalty-free age with traditional money to draw on.
  const seppMode = startAge < PENALTY_FREE_AGE && traditional > 0 && SEPP_MODES.includes(profile.seppMode)
    ? profile.seppMode
    : 'none';

  // Own savings plus the employer match they earn, for each year until retirement.
  const savingRows = buildSavingsPlan(profile, { currentAge, retirementAge: startAge, delayYears: profile.savingsDelayYears });
  const savings = savingRows.map((row) => row.total);
  // Where new savings go: the shares the person chose, with any employer match
  // in a traditional account, or else the mix of the accounts held today.
  const savingsSplit = savingsSplitOf(profile.savingsSplit);
  const ownSaved = savingRows.reduce((sum, row) => sum + row.ownSavings, 0);
  const matchSaved = savingRows.reduce((sum, row) => sum + row.match, 0);

  const claimAge = (value) => clamp(wholeAge(value, fra), 62, 70);
  const pension = (p, ownerAge) => ({
    annual: dollars(p?.annualAmount),
    startAge: clamp(wholeAge(p?.startAge, 65), 40, 100),
    cola: !!p?.cola,
    ownerAge,
    // What a one-off payment in place of the pension would be, if one is offered.
    lumpSum: dollars(p?.lumpSum),
    // Share of the pension that carries on for the other spouse.
    survivorShare: clamp(p?.survivorPct != null && p.survivorPct !== '' ? Number(p.survivorPct) || 0 : 50, 0, 100) / 100,
  });

  // Investing costs and a view on returns both move every year's return.
  const fee = clamp(Number(profile.investmentFee) || 0, 0, MAX_FEE * 100) / 100;
  const outlook = OUTLOOK_SHIFTS[profile.returnOutlook] != null ? profile.returnOutlook : 'average';
  const returnShift = OUTLOOK_SHIFTS[outlook] - fee;
  // A mix that moves toward another one between two ages.
  const glideTo = profile.glide && RISK_PROFILES[profile.glide.to] && profile.glide.to !== riskProfile ? profile.glide.to : null;
  let stockShares = null;
  if (glideTo) {
    const from = clamp(wholeAge(profile.glide.fromAge, 55), 10, 119);
    const to = clamp(wholeAge(profile.glide.toAge, 65), from + 1, 120);
    const a = RISK_PROFILES[riskProfile].stocks;
    const b = RISK_PROFILES[glideTo].stocks;
    stockShares = Array.from({ length: lifeAge - currentAge }, (_, i) => (
      Math.round((a + (b - a) * clamp((currentAge + i - from) / (to - from), 0, 1)) * 1000) / 1000));
  }
  const returnInYear = (i) => (stockShares
    ? expectedReturnForShare(stockShares[Math.min(i, stockShares.length - 1)])
    : RISK_PROFILES[riskProfile].meanReturn / 100) + returnShift;

  // Money that arrives or leaves once, at an age: an inheritance, a house sale, a new roof.
  const lumps = listRows(profile.lumpSums)
    .map((row) => ({ offset: wholeAge(row.age, 0) - currentAge, amount: Math.round(Number(row.amount) || 0), account: 'taxable' }))
    .filter((row) => row.amount !== 0 && row.offset >= 0 && row.offset < lifeAge - currentAge);
  // Income in retirement other than pensions and Social Security.
  const otherIncome = listRows(profile.otherIncome)
    .map((row) => ({
      kind: OTHER_INCOME_KINDS.includes(row.kind) ? row.kind : 'taxable',
      annual: dollars(row.annual),
      fromAge: wholeAge(row.fromAge, startAge),
      toAge: wholeAge(row.toAge, lifeAge),
    }))
    .filter((row) => row.annual > 0 && row.toAge >= row.fromAge);
  // Property held as an investment: its net income is income in retirement,
  // and if it is to be sold, what is left after the loan and the costs of
  // selling arrives as one-off money at that age.
  const properties = listRows(profile.properties)
    .map((row) => ({
      value: dollars(row.value),
      income: dollars(row.netIncome),
      growth: clamp(Number(row.growth) || 0, -5, 10) / 100,
      sellAge: wholeAge(row.sellAge, 0),
      owedAtSale: dollars(row.owedAtSale),
      sellCost: clamp(row.sellCostPct == null || row.sellCostPct === '' ? PROPERTY_SELL_COST * 100 : Number(row.sellCostPct) || 0, 0, 60) / 100,
    }))
    .filter((row) => row.value > 0 || row.income > 0)
    .map((row) => {
      const sold = row.sellAge > currentAge && row.sellAge < lifeAge;
      const years = row.sellAge - currentAge;
      // The price grows with the property; the loan is a fixed number of dollars, so inflation shrinks it.
      const proceeds = sold
        ? Math.max(0, row.value * Math.pow(1 + row.growth, years) * (1 - row.sellCost) - row.owedAtSale / Math.pow(1 + (clamp(simulationConfig.inflationRate != null ? Number(simulationConfig.inflationRate) : 2.5, 0, 15) / 100), years))
        : 0;
      return { ...row, sold, proceeds: Math.round(proceeds), incomeToAge: sold ? row.sellAge - 1 : lifeAge };
    });
  properties.forEach((property) => {
    if (property.income > 0 && property.incomeToAge >= startAge) otherIncome.push({ kind: 'taxable', annual: property.income, fromAge: startAge, toAge: property.incomeToAge });
    if (property.sold && property.proceeds > 0) lumps.push({ offset: property.sellAge - currentAge, amount: property.proceeds, account: 'taxable' });
  });
  // Spending as a share of its starting level from an age on.
  const spendingSteps = listRows(profile.spendingSteps)
    .map((row) => ({ fromAge: wholeAge(row.fromAge, 0), share: clamp(Number(row.percent) || 0, 25, 300) / 100 }))
    .filter((row) => row.fromAge > startAge && row.fromAge < lifeAge)
    .sort((x, y) => x.fromAge - y.fromAge);
  const spousePayStart = married ? dollars(profile.spouse2?.income) : 0;
  const annuityAge = wholeAge(profile.annuity?.age, 65);
  const annuityOn = dollars(profile.annuity?.amount) > 0 && Number(profile.annuity?.payoutRate) > 0
    && annuityAge >= currentAge && annuityAge < lifeAge;

  const state = STATE_TAX_RATES[profile.state] ? profile.state : '';
  // A home that could be sold or borrowed against if savings ran out.
  const homeValue = dollars(profile.home?.value);
  const homeEquity = Math.max(0, homeValue - dollars(profile.home?.owed));
  const homeShare = clamp(profile.home?.releasePct != null && profile.home.releasePct !== '' ? Number(profile.home.releasePct) || 0 : 50, 0, 100) / 100;

  const inputs = {
    filingStatus,
    married,
    currentAge,
    startAge,
    lifeAge,
    spouseAge,
    birthYear,
    yearsToRetirement: startAge - currentAge,
    retirementYears: lifeAge - startAge,
    spending: dollars(profile.annualSpending),
    savings,
    // The assumptions behind that schedule, for reports and the offline calculator.
    pay: {
      start: dollars(profile.income),
      workStartAge: Math.max(currentAge, wholeAge(profile.workStartAge, currentAge)),
      growth: clamp(Number(profile.incomeGrowthRate) || 0, -10, 20) / 100,
      growthUntilAge: wholeAge(profile.incomeGrowthUntilAge, 55),
      savingsMode: profile.savingsMode === 'percent' ? 'percent' : 'amount',
      savingsRate: clamp(Number(profile.savingsRate) || 0, 0, 100) / 100,
      savingsAmount: dollars(profile.annualSavings),
      matchShare: clamp(Number(profile.matchPctOfSalary) || 0, 0, 100) / 100,
      matchRate: clamp(Number(profile.matchRate) || 0, 0, 200) / 100,
    },
    // The first year in which anything is saved, for display.
    annualSavings: savings.find((amount) => amount > 0) || 0,
    savingsGrowthRate: clamp(Number(profile.savingsGrowthRate) || 0, -20, 20) / 100,
    riskProfile,
    // At the start of retirement, after costs and the outlook chosen.
    expectedReturn: returnInYear(startAge - currentAge),
    // Year by year from now, only when the mix changes with age.
    expectedReturns: stockShares ? stockShares.map((_, i) => Math.round(returnInYear(i) * 1e6) / 1e6) : null,
    stockShares,
    glideTo,
    fee,
    outlook,
    returnShift,
    inflation: clamp(simulationConfig.inflationRate != null ? Number(simulationConfig.inflationRate) : 2.5, 0, 15) / 100,
    accounts: { traditional, roth, taxable, total },
    taxableBasis,
    basisEntered,
    rothBasis,
    rothBasisEntered,
    // A flat state income tax rate on retirement income other than Social
    // Security, and the yearly cost of health coverage before Medicare.
    // Used only when no state is chosen.
    stateTaxRate: state ? 0 : clamp(Number(profile.stateTaxRate) || 0, 0, MAX_STATE_TAX_RATE * 100) / 100,
    // Two-letter code: that state's own income tax rules. '' for none chosen.
    state,
    // County income tax on the state's taxable income, only where the state has one.
    localTaxRate: localIncomeTaxOf(state) ? clamp(Number(profile.localTaxRate) || 0, 0, MAX_LOCAL_TAX_RATE * 100) / 100 : 0,
    // Share of traditional money held in IRAs: some states treat it differently from employer plans.
    iraShare: traditional > 0 ? dollars(acct.tradIRA) / traditional : 0,
    healthCostBeforeMedicare: dollars(profile.healthCostBeforeMedicare),
    // Full yearly price of the benchmark marketplace plan: with it, the premium
    // tax credit is worked out from each year's income.
    acaBenchmark: dollars(profile.acaBenchmarkPremium),
    householdSize: (married ? 2 : 1) + clamp(Math.round(Number(profile.dependents) || 0), 0, 10),
    // Health coverage worked out from the household instead of entered: the
    // benchmark premium for whoever is under 65 each year (children to 26),
    // plus what each of them is expected to pay for care itself.
    // When Roth conversions are made: every year as scheduled, or only in a
    // year the portfolio is not far behind the plan's own projection.
    conversionPace: CONVERSION_PACES.includes(profile.conversionPace) ? profile.conversionPace : 'steady',
    // Conversions stop short of the next Medicare surcharge tier in a year whose income sets one.
    conversionIrmaa: !!profile.conversionIrmaa,
    currentYear,
    healthEstimate: profile.healthCoverMode === 'estimate',
    healthOutOfPocket: dollars(profile.healthOutOfPocket),
    childAges: childAgesOf(profile.childrenAges),
    medicarePremiums: !!profile.medicarePremiums,
    mortgage: { annual: dollars(profile.mortgage?.annualPayment), payoffAge: wholeAge(profile.mortgage?.payoffAge, 0), included: !!profile.mortgage?.included },
    care: {
      annual: dollars(profile.longTermCare?.annualCost),
      startAge: wholeAge(profile.longTermCare?.startAge, 0),
      years: clamp(Math.round(Number(profile.longTermCare?.years) || 0), 0, 40),
    },
    lumps,
    otherIncome,
    properties,
    spendingSteps,
    // Pay of a spouse who retires on their own date.
    spousePay: spousePayStart > 0 ? {
      start: spousePayStart,
      retireAge: clamp(wholeAge(profile.spouse2?.retirementAge, 65), 18, 110),
      growth: clamp(Number(profile.incomeGrowthRate) || 0, -10, 20) / 100,
      growthUntilAge: wholeAge(profile.incomeGrowthUntilAge, 55),
    } : null,
    home: homeEquity > 0 ? { value: homeValue, equity: homeEquity, share: homeShare } : null,
    // Set only inside the home-equity comparison: money released once, in the
    // year savings would otherwise run out.
    reserve: 0,
    // The plan must also end with at least this much for a market to count as holding up.
    legacyTarget: dollars(profile.legacyTarget),
    // What a surviving spouse spends, as a share of the household's spending.
    survivorSpending: clamp(profile.survivorSpendingPct != null && profile.survivorSpendingPct !== '' ? Number(profile.survivorSpendingPct) || 0 : 75, 25, 100) / 100,
    // Set only inside the "one of you dies" comparison: { who: 1 or 2, y: retirement year }.
    survivor: null,
    // Age (the owner's) at which that comparison is made. 0: ten years into retirement.
    survivorAge: wholeAge(profile.survivorAge, 0),
    // A what-if: buying a lifetime income with part of the portfolio.
    annuity: annuityOn ? { amount: dollars(profile.annuity.amount), age: annuityAge, rate: clamp(Number(profile.annuity.payoutRate), 0, 25) / 100 } : null,
    // Share of traditional money that is a 401(k) free of the penalty under the rule of 55.
    penaltyFreeTraditionalShare: ruleOf55 ? dollars(acct.trad401k) / traditional : 0,
    withdrawalOrder: WITHDRAWAL_ORDERS.includes(profile.withdrawalOrder) ? profile.withdrawalOrder : 'proportional',
    rothConversion: requestedConversion,
    sepp: { mode: seppMode, amount: seppMode === 'custom' ? dollars(profile.seppAmount) : 0 },
    strategy: {
      bucketYears: Math.round(setting('bucketYears', 1, 10)),
      guardrailBand: setting('guardrailBand', 5, 50) / 100,
      guardrailCut: setting('guardrailCut', 1, 20) / 100,
      guardrailRaise: setting('guardrailRaise', 0, 25) / 100,
      vanguardCeiling: setting('vanguardCeiling', 0, 20) / 100,
      vanguardFloor: setting('vanguardFloor', 0, 20) / 100,
    },
    // Today's mix of account types.
    shares: total > 0
      ? { traditional: traditional / total, roth: roth / total, taxable: taxable / total }
      : { traditional: 1, roth: 0, taxable: 0 },
    // The chosen shares of new savings (null: none chosen), and the shares all
    // savings, employer match included, are added in.
    savingsSplit,
    savingsShares: savingsSplit
      ? (ownSaved + matchSaved > 0
        ? {
          traditional: (ownSaved * savingsSplit.traditional + matchSaved) / (ownSaved + matchSaved),
          roth: (ownSaved * savingsSplit.roth) / (ownSaved + matchSaved),
          taxable: (ownSaved * savingsSplit.taxable) / (ownSaved + matchSaved),
        }
        : savingsSplit)
      : (total > 0
        ? { traditional: traditional / total, roth: roth / total, taxable: taxable / total }
        : { traditional: 1, roth: 0, taxable: 0 }),
    socialSecurity: [
      { monthlyAtFRA: dollars(profile.ss1), claimAge: claimAge(profile.ss1ClaimAge), ownerAge: currentAge, birthYear },
      ...(married ? [{
        monthlyAtFRA: dollars(profile.ss2),
        claimAge: claimAge(profile.ss2ClaimAge),
        ownerAge: spouseAge,
        birthYear: currentYear - spouseAge,
      }] : []),
    ],
    pensions: [
      pension(profile.pension1, currentAge),
      ...(married ? [pension(profile.pension2, spouseAge)] : []),
    ],
    rmdAge: birthYear >= 1960 ? 75 : birthYear <= 1950 ? 72 : 73,
    fullRetirementAge: fra,
  };
  return { ...inputs, key: JSON.stringify(inputs) };
}

/** The age at which the plan first adds savings, or null if it never does. */
export function firstSavingAge(inputs) {
  const index = inputs.savings.findIndex((amount) => amount > 0);
  return index < 0 ? null : inputs.currentAge + index;
}

// ── Guaranteed income, in today's dollars ──────────────────────────────────

/** One-off money before retirement, per year from now: the total, and by the account it lands in. */
export function lumpsBefore(inputs) {
  const years = inputs.yearsToRetirement;
  const out = { total: new Array(years).fill(0), taxable: new Array(years).fill(0), traditional: new Array(years).fill(0) };
  for (const lump of inputs.lumps || []) {
    if (lump.offset < 0 || lump.offset >= years) continue;
    out.total[lump.offset] += lump.amount;
    out[lump.account === 'traditional' ? 'traditional' : 'taxable'][lump.offset] += lump.amount;
  }
  return out;
}

/**
 * Everything a retirement year brings in or costs apart from the spending
 * target itself, in today's dollars:
 *   pension  taxable income that is not Social Security (pensions, pay, rent)
 *   ss       Social Security
 *   fixed    costs on top of spending (health coverage, a mortgage, care, one-off
 *            bills, payroll tax) less income that is not taxed
 *   factor   spending as a share of its starting level that year
 *   inflow   one-off money arriving (after tax; `inflowTraditional` is pre-tax
 *            money moving into or out of traditional accounts)
 *   filing   filing status that year, which changes after a death
 * `parts` holds the pieces for display.
 */
export function buildIncome(inputs, claimAgeOverride = null) {
  const n = inputs.retirementYears;
  const zeros = () => new Array(n).fill(0);
  const pension = zeros();
  const ss = zeros();
  const fixed = zeros();
  const inflow = zeros();
  const inflowTraditional = zeros();
  const factor = new Array(n).fill(1);
  const filing = new Array(n).fill(inputs.filingStatus);
  const spousePresent = new Array(n).fill(inputs.spouseAge != null);
  // For the premium tax credit: the full price of the benchmark plan each
  // year, the household it is measured against, and how many are covered.
  const healthBenchmark = zeros();
  const household = new Array(n).fill(inputs.householdSize);
  const healthCovered = zeros();
  const parts = {
    pension: zeros(), work: zeros(), other: zeros(), taxFree: zeros(),
    health: zeros(), medicare: zeros(), mortgage: zeros(), care: zeros(), oneTime: zeros(), payroll: zeros(),
    // Spending that stops (a mortgage inside the spending figure, once paid off).
    relief: zeros(),
  };
  const gone = inputs.survivor || null;
  const paidOff = inputs.mortgage?.included && inputs.mortgage.annual > 0 && inputs.mortgage.payoffAge > inputs.startAge;
  // What the payment was worth in its last year, in today's dollars: the amount
  // by which the year's need falls once it is paid off. A fixed number of
  // dollars, like any other cost, so what the plan can spend never depends on
  // the spending figure entered.
  const mortgageRelief = paidOff
    ? inputs.mortgage.annual / Math.pow(1 + inputs.inflation, Math.max(0, inputs.mortgage.payoffAge - 1 - inputs.currentAge))
    : 0;

  for (let y = 0; y < n; y++) {
    const yearsFromNow = inputs.yearsToRetirement + y;
    const age = inputs.startAge + y;
    const widowed = gone != null && y >= gone.y;
    // Index of the person who has died: 0 is the plan's owner, 1 the spouse.
    const lost = widowed ? gone.who - 1 : -1;

    inputs.pensions.forEach((p, i) => {
      const ownerAge = p.ownerAge + yearsFromNow;
      if (p.annual > 0 && ownerAge >= p.startAge) {
        // The amount entered is in today's dollars: what it pays now if it has
        // started, or what it will pay at the start age. Without cost-of-living
        // increases it loses purchasing power every year after that.
        const erodingSince = Math.max(p.startAge, p.ownerAge);
        const amount = p.cola ? p.annual : p.annual / Math.pow(1 + inputs.inflation, ownerAge - erodingSince);
        parts.pension[y] += i === lost ? amount * (p.survivorShare ?? 0.5) : amount;
      }
    });

    const benefits = inputs.socialSecurity.map((s, i) => {
      const claimAge = i === 0 && claimAgeOverride != null ? claimAgeOverride : s.claimAge;
      const age = s.ownerAge + yearsFromNow;
      const own = s.monthlyAtFRA > 0 ? adjustBenefitForClaimingAge(s.monthlyAtFRA, claimAge, s.birthYear) * 12 : 0;
      return { ...s, claimAge, age, own, claimed: age >= claimAge };
    });
    if (lost >= 0 && benefits.length === 2) {
      // The survivor keeps the larger of their own benefit and the survivor
      // benefit, which starts at 60 at the earliest; any spousal amount stops.
      const keeps = benefits[1 - lost];
      const died = benefits[lost];
      const deathAge = died.ownerAge + inputs.yearsToRetirement + gone.y;
      const startAge = Math.max(keeps.ownerAge + inputs.yearsToRetirement + gone.y, SURVIVOR_BENEFIT_AGE);
      const survivor = keeps.age >= SURVIVOR_BENEFIT_AGE
        ? survivorBase(died.monthlyAtFRA, died.claimAge, deathAge, died.birthYear) * survivorFactor(startAge, keeps.birthYear) * 12
        : 0;
      ss[y] = Math.max(keeps.claimed ? keeps.own : 0, survivor);
    } else {
      ss[y] = benefits.reduce((sum, b, i) => {
        if (!b.claimed) return sum;
        // A spouse whose own benefit is under half the other's gets the
        // difference once both have claimed, cut if claimed early.
        const other = benefits.length === 2 ? benefits[1 - i] : null;
        const spousal = other && other.claimed ? spousalBenefit(b.monthlyAtFRA, other.monthlyAtFRA, b.claimAge, b.birthYear) * 12 : 0;
        return sum + b.own + spousal;
      }, 0);
    }

    // A stress test: benefits paid at a share of what is scheduled from a calendar year on.
    if (inputs.ssCut && yearsFromNow >= inputs.ssCut.fromYear) ss[y] *= inputs.ssCut.factor;

    for (const stream of inputs.otherIncome || []) {
      if (age >= stream.fromAge && age <= stream.toAge) {
        parts[stream.kind === 'work' ? 'work' : stream.kind === 'taxFree' ? 'taxFree' : 'other'][y] += stream.annual;
      }
    }
    const spousePay = inputs.spousePay;
    let spouseWork = 0;
    if (spousePay && inputs.spouseAge != null && lost !== 1) {
      const spouseAge = inputs.spouseAge + yearsFromNow;
      if (spouseAge < spousePay.retireAge) {
        spouseWork = spousePay.start * Math.pow(1 + spousePay.growth, Math.max(0, Math.min(spouseAge, spousePay.growthUntilAge) - inputs.spouseAge));
        parts.work[y] += spouseWork;
      }
    }
    const ownWork = parts.work[y] - spouseWork;
    parts.payroll[y] = MEDICARE_PAYROLL_RATE * parts.work[y]
      + (PAYROLL_TAX_RATE - MEDICARE_PAYROLL_RATE) * (Math.min(ownWork, SS_WAGE_BASE) + Math.min(spouseWork, SS_WAGE_BASE));
    pension[y] = parts.pension[y] + parts.work[y] + parts.other[y];

    if (inputs.healthEstimate) {
      // Whoever is alive, under 65 (a child: under 26) is on marketplace coverage that year.
      const adults = [lost !== 0 ? age : null, inputs.spouseAge != null && lost !== 1 ? inputs.spouseAge + yearsFromNow : null]
        .filter((a) => a != null && a < MEDICARE_AGE);
      const kids = (inputs.childAges || []).map((a) => a + yearsFromNow).filter((a) => a < CHILD_COVER_END_AGE);
      const onPlan = adults.length + kids.length;
      healthBenchmark[y] = onPlan ? benchmarkPremium(inputs.state, [...adults, ...kids]) : 0;
      household[y] = (inputs.spouseAge != null && lost < 0 ? 2 : 1) + kids.length;
      parts.health[y] = healthBenchmark[y] + onPlan * (inputs.healthOutOfPocket || 0);
      healthCovered[y] = onPlan;
    } else {
      if (age < MEDICARE_AGE) parts.health[y] = inputs.healthCostBeforeMedicare || 0;
      healthBenchmark[y] = parts.health[y] > 0 ? inputs.acaBenchmark || 0 : 0;
      // Children entered by age are counted while they can be on the plan.
      if (inputs.childAges?.length) {
        household[y] = (inputs.spouseAge != null && lost < 0 ? 2 : 1)
          + inputs.childAges.filter((a) => a + yearsFromNow < CHILD_COVER_END_AGE).length;
      }
    }
    if (inputs.medicarePremiums) {
      const enrolled = (lost !== 0 && age >= MEDICARE_AGE ? 1 : 0)
        + (inputs.spouseAge != null && lost !== 1 && inputs.spouseAge + yearsFromNow >= MEDICARE_AGE ? 1 : 0);
      parts.medicare[y] = enrolled * MEDICARE_IRMAA.standardPartBPremium * 12;
    }
    // A mortgage payment is a fixed number of dollars, so inflation shrinks it.
    if (inputs.mortgage?.annual > 0 && age < inputs.mortgage.payoffAge && !inputs.mortgage.included) {
      parts.mortgage[y] = inputs.mortgage.annual / Math.pow(1 + inputs.inflation, yearsFromNow);
    }
    if (inputs.care?.annual > 0 && age >= inputs.care.startAge && age < inputs.care.startAge + inputs.care.years) {
      parts.care[y] = inputs.care.annual;
    }
    fixed[y] = parts.health[y] + parts.medicare[y] + parts.mortgage[y] + parts.care[y] + parts.payroll[y] - parts.taxFree[y];

    let share = 1;
    for (const step of inputs.spendingSteps || []) if (age >= step.fromAge) share = step.share;
    if (widowed) share *= inputs.survivorSpending ?? 0.75;
    // A mortgage already inside the spending figure: once it is paid off,
    // the year needs that much less. A fixed sum, never tied to the spending
    // figure, so what the plan could spend does not depend on the target.
    if (mortgageRelief > 0 && age >= inputs.mortgage.payoffAge) {
      parts.relief[y] = mortgageRelief;
      fixed[y] -= parts.relief[y];
    }
    factor[y] = share;
    // The year of a death is still a joint return; after it, a single one.
    if (gone != null && y > gone.y) {
      if (isMarriedStatus(inputs.filingStatus)) filing[y] = 'single';
      spousePresent[y] = false;
    }
  }

  for (const lump of inputs.lumps || []) {
    const y = lump.offset - inputs.yearsToRetirement;
    if (y < 0 || y >= n) continue;
    if (lump.account === 'traditional') inflowTraditional[y] += lump.amount;
    else if (lump.amount > 0) inflow[y] += lump.amount;
    else {
      parts.oneTime[y] -= lump.amount;
      fixed[y] -= lump.amount;
    }
  }
  return { pension, ss, fixed, factor, filing, spousePresent, inflow, inflowTraditional, parts, healthBenchmark, household, healthCovered };
}

// Income above which a household of this size gets no premium tax credit at all.
export const acaCliffIncome = (householdSize) => ACA.cliff * (ACA.povertyLine.first + ACA.povertyLine.additional * Math.max(0, householdSize - 1));
// A plan that aims at the limit stays this far under it: going one dollar over loses the whole credit.
export const CREDIT_LIMIT_MARGIN = 500;

/** Premium tax credit for a year of marketplace coverage, from that year's income. */
export function acaCredit(magi, householdSize, benchmark) {
  if (!(benchmark > 0)) return 0;
  const line = ACA.povertyLine.first + ACA.povertyLine.additional * Math.max(0, householdSize - 1);
  const ratio = Math.max(0, magi) / line;
  // The credit is for incomes from the poverty line to the limit. Below the
  // line there is none (Medicaid, where a state has widened it; nothing, where not).
  if (ratio > ACA.cliff || ratio < 1) return 0;
  let share = ACA.floorShare;
  const points = ACA.schedule;
  if (ratio >= points[0][0]) {
    share = points[points.length - 1][1];
    for (let i = 1; i < points.length; i++) {
      if (ratio <= points[i][0]) {
        const [x0, y0] = points[i - 1];
        const [x1, y1] = points[i];
        share = y0 + (y1 - y0) * (ratio - x0) / (x1 - x0);
        break;
      }
    }
  }
  return Math.max(0, benchmark - share * Math.max(0, magi));
}

// The credit in a retirement year, from the tax result for that year. It can
// never be worth more than the cover costs.
const creditFor = (inputs, income, y, result) => (income.healthBenchmark[y] > 0 && income.parts.health[y] > 0
  ? Math.min(income.parts.health[y], acaCredit(result.agi + income.ss[y] - result.taxableSS, income.household[y], income.healthBenchmark[y]))
  : 0);

// ── Federal income tax on a retirement year ────────────────────────────────

function bracketTax(taxable, brackets) {
  let tax = 0;
  let marginalRate = 0;
  for (const { rate, min, max } of brackets) {
    if (taxable <= min) break;
    tax += (Math.min(max, taxable) - min) * rate;
    marginalRate = rate;
  }
  return { tax, marginalRate };
}

/**
 * The 2025 to 2028 deduction for each person 65 or over (`SENIOR_DEDUCTION`),
 * for the calendar `year` being taxed. Its income threshold is fixed in
 * dollars, so in today's dollars it shrinks with `prices`.
 */
export function seniorDeduction(filingStatus, age, spouseAge, magi, prices = 1, year = null) {
  const rule = SENIOR_DEDUCTION;
  if (year == null || year < rule.firstYear || year > rule.lastYear) return 0;
  const threshold = rule.threshold[filingStatus];
  if (threshold == null) return 0;
  let people = age >= 65 ? 1 : 0;
  if (filingStatus === 'married_filing_jointly' && spouseAge != null && spouseAge >= 65) people += 1;
  const each = Math.max(0, rule.amount - rule.phaseOutRate * Math.max(0, magi - threshold / prices));
  return people * each;
}

export function standardDeduction(filingStatus, age, spouseAge) {
  const entry = STANDARD_DEDUCTION[filingStatus] || STANDARD_DEDUCTION.married_filing_jointly;
  let seniors = age >= 65 ? 1 : 0;
  if (filingStatus === 'married_filing_jointly' && spouseAge != null && spouseAge >= 65) seniors += 1;
  return entry.amount + seniors * entry.additional;
}

/**
 * Federal tax for one year. `ordinary` is pension + traditional withdrawals +
 * Roth conversions; `gains` is the long-term gain realised from taxable accounts.
 */
// Prices in a retirement year against today's, at the plan's inflation.
export const priceLevel = (inputs, y) => Math.pow(1 + (inputs.inflation || 0), inputs.yearsToRetirement + y);
// The calendar year of a retirement year, for rules that apply to some years only.
export const taxYear = (inputs, y) => (inputs.currentYear ?? new Date().getFullYear()) + inputs.yearsToRetirement + y;

export function federalTax(args) {
  const federal = federalOnly(args);
  // A stress test: federal income tax higher across the board.
  if (args.taxFactor && args.taxFactor !== 1) federal.tax *= args.taxFactor;
  return withStateTax(federal, args);
}

/**
 * Adds state income tax. With `state` (a two-letter code) it is that state's
 * own brackets, deductions and treatment of retirement income
 * (`stateIncomeTax`); `pensionIncome` and `planIncome` are the parts of
 * `ordinary` that are a pension and traditional-account money. Otherwise `stateRate` is a flat rate on
 * ordinary income and gains above the federal standard deduction, with Social
 * Security left out. `localRate` adds a county income tax where the state has
 * one (Maryland, Indiana). `tax` then holds both; `stateTax` holds the state part, county included.
 */
function withStateTax(result, args) {
  const { ordinary = 0, gains = 0, stateRate = 0, state = '', localRate = 0 } = args;
  let stateTax = 0;
  if (state) {
    // A state that does not raise its brackets with prices: the year's income
    // in that year's dollars against today's table, brought back.
    const k = stateBracketsFixed(state) ? args.prices || 1 : 1;
    stateTax = stateIncomeTax({
      state, filingStatus: args.filingStatus, age: args.age, spouseAge: args.spouseAge,
      ordinary: ordinary * k, pension: (args.pensionIncome || 0) * k, plan: (args.planIncome || 0) * k, iraShare: args.iraShare || 0, gains: gains * k,
      ss: (args.ss || 0) * k, taxableSS: result.taxableSS * k, agi: result.agi * k, federalTax: result.tax * k, localRate,
    }) / k;
  } else if (stateRate > 0) {
    stateTax = stateRate * Math.max(0, ordinary + gains - result.deduction);
  }
  return { ...result, tax: result.tax + stateTax, stateTax };
}

function federalOnly(args) {
  if (args.filingStatus !== 'married_filing_separately') return taxOneReturn(args);
  // Two returns. Household income is entered as one figure, so each spouse
  // is taken to report half of it, with their own age-based deduction.
  const half = { ordinary: (args.ordinary || 0) / 2, ss: (args.ss || 0) / 2, gains: (args.gains || 0) / 2, filingStatus: args.filingStatus, prices: args.prices, year: args.year };
  const a = taxOneReturn({ ...half, age: args.age });
  const b = taxOneReturn({ ...half, age: args.spouseAge ?? args.age });
  return {
    tax: a.tax + b.tax,
    marginalRate: Math.max(a.marginalRate, b.marginalRate),
    taxableSS: a.taxableSS + b.taxableSS,
    deduction: a.deduction + b.deduction,
    ordinaryTaxable: a.ordinaryTaxable + b.ordinaryTaxable,
    agi: a.agi + b.agi,
  };
}

// `prices` is the price level of the year being taxed against today's (1 now,
// about 1.28 ten years on at 2.5% inflation). Everything here is in today's
// dollars, which is the same as limits that rise with prices, and most of the
// tax code's do. A few are fixed in dollars by law and never raised: the
// income above which Social Security is taxed, and the income above which the
// tax on investment income applies. In today's dollars those shrink each year.
function taxOneReturn({ ordinary = 0, ss = 0, gains = 0, filingStatus, age, spouseAge, prices = 1, year = null }) {
  // The same sum in that year's dollars against the fixed thresholds, brought back.
  const taxableSS = ss > 0
    ? calculateSocialSecurityTax({ ssaBenefit: ss * prices, otherIncome: (ordinary + gains) * prices, filingStatus }).taxableAmount / prices
    : 0;
  const ordinaryIncome = ordinary + taxableSS;
  const deduction = standardDeduction(filingStatus, age, spouseAge)
    + seniorDeduction(filingStatus, age, spouseAge, ordinaryIncome + gains, prices, year);
  const ordinaryTaxable = Math.max(0, ordinaryIncome - deduction);
  // Deduction left over after ordinary income shelters gains.
  const gainsTaxable = Math.max(0, gains - Math.max(0, deduction - ordinaryIncome));

  const brackets = FEDERAL_TAX_BRACKETS[filingStatus] || FEDERAL_TAX_BRACKETS.married_filing_jointly;
  const ordinaryResult = bracketTax(ordinaryTaxable, brackets);

  // Gains stack on top of ordinary taxable income through the 0/15/20% tiers.
  let gainsTax = 0;
  if (gainsTaxable > 0) {
    const tiers = CAPITAL_GAINS_BRACKETS[filingStatus] || CAPITAL_GAINS_BRACKETS.married_filing_jointly;
    const top = ordinaryTaxable + gainsTaxable;
    for (const { rate, min, max } of tiers) {
      const from = Math.max(min, ordinaryTaxable);
      const to = Math.min(max, top);
      if (to > from) gainsTax += (to - from) * rate;
    }
  }

  const agi = ordinaryIncome + gains;
  const niitThreshold = (NIIT_THRESHOLD[filingStatus] ?? NIIT_THRESHOLD.married_filing_jointly) / prices;
  const niit = NIIT_THRESHOLD.rate * Math.min(gains, Math.max(0, agi - niitThreshold));

  return {
    tax: ordinaryResult.tax + gainsTax + niit,
    marginalRate: ordinaryResult.marginalRate,
    taxableSS,
    deduction,
    ordinaryTaxable,
    agi,
  };
}

/**
 * The income at which the next Medicare surcharge tier starts, above `agi`
 * (null above the last one). Filing separately, each spouse's half is measured.
 */
function nextIrmaaTier(agi, filingStatus) {
  const tiers = MEDICARE_IRMAA[filingStatus] || MEDICARE_IRMAA.married_filing_jointly;
  const scale = filingStatus === 'married_filing_separately' ? 2 : 1;
  const tier = tiers.find((t) => t.min > 0 && agi < t.min * scale);
  return tier ? tier.min * scale : null;
}

function annualIrmaa(agi, filingStatus, age, spouseAge) {
  const tiers = MEDICARE_IRMAA[filingStatus] || MEDICARE_IRMAA.married_filing_jointly;
  // Filing separately, each spouse is measured on their own half.
  const measured = filingStatus === 'married_filing_separately' ? agi / 2 : agi;
  let surcharge = 0;
  for (const tier of tiers) if (measured > tier.min) surcharge = tier.surcharge;
  let enrolled = age >= 65 ? 1 : 0;
  if (isMarriedStatus(filingStatus) && spouseAge != null && spouseAge >= 65) enrolled += 1;
  return surcharge * 12 * enrolled;
}

// ── Pre-tax <-> after-tax conversion for the simulations ───────────────────
//
// The simulations hold one pooled balance. A withdrawal D in a given year is
// split across account types the way the account projection splits that
// year's withdrawal at the same spending level (`mix`), taxed, and added to
// after-tax guaranteed income. Both directions are tabulated once per year of
// retirement, because the hot loop asks millions of times.

const DRAW_GRID = (() => {
  const grid = [0];
  for (let v = 250; v < 2e7; v *= 1.06) grid.push(v);
  return grid;
})();

export function buildTaxEnv(inputs, income, mix = withdrawalMix(projectAccounts(inputs, { income }), inputs)) {
  const n = inputs.retirementYears;
  const G = DRAW_GRID.length;
  const table = new Float64Array(n * G);
  for (let y = 0; y < n; y++) {
    const age = inputs.startAge + y;
    const filingStatus = income.filing[y];
    const spouseAge = inputs.spouseAge != null && income.spousePresent[y] ? inputs.spouseAge + inputs.yearsToRetirement + y : null;
    let previous = -Infinity;
    for (let k = 0; k < G; k++) {
      const draw = DRAW_GRID[k];
      // A 72(t) payment is a fixed amount that comes out whether or not it is
      // needed (anything beyond the year's draw is taxed and stays invested).
      // The rest of the draw is split across accounts by the year's mix.
      const rest = Math.max(0, draw - mix.sepp[y]);
      const penalty = age < PENALTY_FREE_AGE ? EARLY_WITHDRAWAL_PENALTY * mix.penalized[y] * rest : 0;
      const result = federalTax({
        ordinary: income.pension[y] + mix.sepp[y] + (mix.traditional[y] + mix.rothTaxed[y]) * rest + mix.conversion[y],
        ss: income.ss[y],
        gains: mix.gainsFraction[y] * mix.taxable[y] * rest,
        filingStatus, age, spouseAge, stateRate: inputs.stateTaxRate, state: inputs.state, localRate: inputs.localTaxRate, prices: priceLevel(inputs, y), year: taxYear(inputs, y), taxFactor: inputs.taxFactor,
        pensionIncome: income.parts.pension[y], planIncome: mix.sepp[y] + mix.traditional[y] * rest + mix.conversion[y], iraShare: inputs.iraShare,
      });
      // What is left after tax, the penalty and the year's other costs, as a
      // spending level: divided by the share of the starting level spent that
      // year. The guard keeps the inverse lookup monotone across tax cliffs
      // (and the point where a health credit is lost).
      // The Medicare surcharge is set by income two years earlier; a path's
      // income moves slowly, so the year's own income stands in for it, as the
      // projection does in a plan's first two years.
      const surcharge = annualIrmaa(result.agi, filingStatus, age, spouseAge);
      const left = income.pension[y] + income.ss[y] + draw - result.tax - penalty - surcharge - income.fixed[y] + creditFor(inputs, income, y, result);
      previous = Math.max(previous, left / income.factor[y]);
      table[y * G + k] = previous;
    }
  }

  const spendFor = (y, draw) => {
    const base = y * G;
    if (draw <= 0) return table[base];
    let lo = 0;
    let hi = G - 1;
    if (draw >= DRAW_GRID[hi]) {
      const slope = (table[base + hi] - table[base + hi - 1]) / (DRAW_GRID[hi] - DRAW_GRID[hi - 1]);
      return table[base + hi] + (draw - DRAW_GRID[hi]) * slope;
    }
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (DRAW_GRID[mid] <= draw) lo = mid; else hi = mid;
    }
    const f = (draw - DRAW_GRID[lo]) / (DRAW_GRID[hi] - DRAW_GRID[lo]);
    return table[base + lo] + f * (table[base + hi] - table[base + lo]);
  };

  const drawFor = (y, spend) => {
    const base = y * G;
    if (spend <= table[base]) return 0;
    let lo = 0;
    let hi = G - 1;
    if (spend >= table[base + hi]) {
      const slope = (table[base + hi] - table[base + hi - 1]) / (DRAW_GRID[hi] - DRAW_GRID[hi - 1]);
      return DRAW_GRID[hi] + (spend - table[base + hi]) / slope;
    }
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (table[base + mid] <= spend) lo = mid; else hi = mid;
    }
    const span = table[base + hi] - table[base + lo];
    const f = span > 0 ? (spend - table[base + lo]) / span : 0;
    return DRAW_GRID[lo] + f * (DRAW_GRID[hi] - DRAW_GRID[lo]);
  };

  const inflow = income.inflow.map((amount, y) => amount + income.inflowTraditional[y]);
  return { spendFor, drawFor, factor: income.factor, inflow, reserve: inputs.reserve || 0 };
}

// ── Simulated market paths ─────────────────────────────────────────────────

// The simulated markets depend only on the allocation and inflation, never on
// the plan being tested. Changing spending, a claiming age or the retirement
// age therefore reruns the same market histories, and the difference in the
// answer is the effect of the change and nothing else.
export const marketSeed = (inputs) => hashSeed(`markets|${inputs.riskProfile}|${inputs.inflation}`);

/**
 * Each path carries the portfolio through its own accumulation years, so the
 * balance at retirement is as uncertain as the market that produced it.
 */
export function buildPaths(inputs, numSims = DEFAULT_NUM_SIMS, sequences = null) {
  const offset = inputs.yearsToRetirement;
  const n = inputs.retirementYears;
  const all = sequences || simulatedMarkets(inputs, offset + n, numSims);
  const shift = inputs.returnShift || 0;
  const lumps = lumpsBefore(inputs).total;

  const start = new Float64Array(all.length);
  const returns = all.map((path, i) => {
    start[i] = grow(inputs, path, shift, lumps);
    const slice = path.slice(offset, offset + n);
    if (shift !== 0) for (let y = 0; y < slice.length; y++) slice[y] += shift;
    return slice;
  });
  return { start, returns, count: all.length };
}

/** The simulated markets for a plan: its mix (and any change in it with age) and its inflation. */
export function simulatedMarkets(inputs, years, numSims = DEFAULT_NUM_SIMS) {
  return generateCorrelatedSequences({
    years,
    riskProfile: inputs.riskProfile,
    stockShares: inputs.stockShares,
    inflationMean: inputs.inflation,
    numSims,
    seed: marketSeed(inputs),
  }).allPaths;
}

// Savings at retirement down one market: returns after costs, the savings
// schedule, and any one-off money on the way.
function grow(inputs, path, shift, lumps) {
  let balance = inputs.accounts.total;
  for (let y = 0; y < inputs.yearsToRetirement; y++) {
    balance = Math.max(0, balance * (1 + path[y] + shift) + inputs.savings[y] + lumps[y]);
  }
  return Math.max(0, balance);
}

const percentile = (sorted, p) => sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor(p * sorted.length)))];
const sortedCopy = (values) => Float64Array.from(values).sort();

/**
 * Savings at the planned retirement age across the standard set of simulated
 * markets: the one figure every screen reports, whatever number of markets
 * that screen runs for its own answers.
 */
export function savingsAtRetirement(inputs) {
  const years = inputs.yearsToRetirement;
  const all = years > 0 ? simulatedMarkets(inputs, years) : [[]];
  const lumps = lumpsBefore(inputs).total;
  const start = sortedCopy(all.map((path) => grow(inputs, path, inputs.returnShift || 0, lumps)));
  return { p10: percentile(start, 0.10), median: percentile(start, 0.5), p90: percentile(start, 0.90) };
}

// ── Withdrawal strategies ──────────────────────────────────────────────────

/**
 * A tax table for a plan whose Roth conversions wait for the market.
 *
 * The plain table follows the projection's conversions in dollars whatever a
 * simulated market does. Here each simulated year decides for itself: the
 * year's conversion is made only if the portfolio is at least
 * `CONVERSION_PACE_FLOOR` of the projection's balance at that age. Three
 * tables stand behind it, from the same spending level:
 *   none      the plan with no conversions at all
 *   history   the plan with every earlier conversion made, none this year
 *   full      the plan with this year's conversion as well
 * A simulated year sits between them: `made` is the share of the conversion
 * dollars planned so far that this market has actually converted, which is
 * how much of the lasting effect (less traditional money, so less tax on
 * later withdrawals) it has earned; `now` is whether it converts this year,
 * which is what it pays for. Spending for a given withdrawal is
 *   (1 - made) x none + (made - now) x history + now x full
 * so a market that always converts reads the full table, and one that never
 * does reads the no-conversion table. A skipped conversion is not caught up.
 */
function buildPacedEnv(inputs, income, projection, level) {
  const n = inputs.retirementYears;
  const mix = withdrawalMix(projection, inputs);
  const full = buildTaxEnv(inputs, income, mix);
  const history = buildTaxEnv(inputs, income, { ...mix, conversion: new Float64Array(n) });
  const none = buildTaxEnv(inputs, income, withdrawalMix(projectAccounts(inputs, { spending: level, income, fillToRate: null }), inputs));
  const planned = mix.conversion;
  // Catching up: a year whose limit is a bracket can take up to one more
  // year's worth on top of its own. Past a limit with a cliff it never does.
  const room = projection.rows.map((row, y) => (inputs.conversionPace === 'catchUp' && row.conversionLimit === 'bracket' ? planned[y] : 0));
  const double = room.some((value) => value > 0)
    ? buildTaxEnv(inputs, income, { ...mix, conversion: planned.map((value, y) => value + room[y]) })
    : null;
  const reference = projection.rows.map((row) => row.startTraditional + row.startRoth + row.startTaxable);
  const floor = inputs.conversionPaceFloor ?? CONVERSION_PACE_FLOOR;
  let made = 1;
  let now = 1;
  let extra = 0;
  let done = 0;
  let due = 0;
  let trace = null;

  // Never below nothing: the blend can dip under zero at small draws, where the
  // tables' differences (this year's conversion tax) outweigh what is drawn.
  const spendFor = (y, draw) => {
    if (made === 1 && now === 1 && extra === 0) return full.spendFor(y, draw);
    const fullSpend = full.spendFor(y, draw);
    const blend = (1 - made) * none.spendFor(y, draw) + (made - now) * history.spendFor(y, draw) + now * fullSpend;
    return Math.max(0, extra > 0 ? blend + extra * (double.spendFor(y, draw) - fullSpend) : blend);
  };
  const drawFor = (y, spend) => {
    if (made === 1 && now === 1 && extra === 0) return full.drawFor(y, spend);
    // Start from the same blend of the three answers, then close in on it.
    let draw = Math.max(0, (1 - made) * none.drawFor(y, spend) + (made - now) * history.drawFor(y, spend) + now * full.drawFor(y, spend)
      + (extra > 0 ? extra * (double.drawFor(y, spend) - full.drawFor(y, spend)) : 0));
    // One correction lands within a few dollars; more only where the tables bend sharply.
    for (let i = 0; i < 3; i++) {
      const got = spendFor(y, draw);
      const gap = spend - got;
      if (Math.abs(gap) < (i === 0 ? 1 : 150)) break;
      const step = Math.max(50, draw * 0.01);
      const slope = (spendFor(y, draw + step) - got) / step;
      draw = Math.max(0, draw + gap / Math.max(0.2, slope));
    }
    // Where the blend bends too sharply for that (a tax cliff, the end of the
    // early-withdrawal penalty), bracket the answer and halve: the draw that
    // first gives the spending asked for.
    if (spendFor(y, draw) < spend - 150) {
      let lo = draw;
      let hi = Math.max(draw * 2, spend, 1000);
      for (let i = 0; i < 40 && spendFor(y, hi) < spend; i++) { lo = hi; hi *= 2; }
      for (let i = 0; i < 30 && hi - lo > 1; i++) {
        const mid = (lo + hi) / 2;
        if (spendFor(y, mid) < spend) lo = mid; else hi = mid;
      }
      draw = hi;
    }
    return draw;
  };
  const pace = {
    // Outside a simulated market, the plan reads as projected: every conversion made.
    asProjected() { made = 1; now = 1; extra = 0; },
    begin() { done = 0; due = 0; },
    // Records, year by year, the share of the planned conversion each year made (more than 1 when catching up).
    track(into) { trace = into; },
    enter(y, balance) {
      made = due > 0 ? done / due : 0;
      now = planned[y] > 0 && balance >= floor * reference[y] ? 1 : 0;
      // Skipped dollars made up this year, as a share of this year's own conversion.
      extra = now && room[y] > 0 ? Math.min(room[y], due - done) / planned[y] : 0;
      due += planned[y];
      done += (now + extra) * planned[y];
      if (trace) trace[y] = now + extra;
    },
    // Share of the planned conversion dollars this market has made so far.
    share: () => (due > 0 ? done / due : 1),
  };
  return { spendFor, drawFor, factor: full.factor, inflow: full.inflow, reserve: full.reserve, pace };
}

function strategyTables(inputs) {
  const n = inputs.retirementYears;
  const r = inputs.expectedReturn;
  // Annuity-due payout rate with k years remaining (withdrawal at start of year).
  const vpwRate = new Float64Array(n + 1);
  for (let k = 1; k <= n; k++) {
    vpwRate[k] = r === 0 ? 1 / k : (r / (1 - Math.pow(1 + r, -k))) / (1 + r);
  }
  // IRS Uniform Lifetime divisors, continued smoothly below the table's first age.
  const rmdDivisor = new Float64Array(n);
  for (let y = 0; y < n; y++) {
    const age = inputs.startAge + y;
    rmdDivisor[y] = Math.max(1, RMD_TABLE[age] != null ? RMD_TABLE[age] : RMD_TABLE[72] + (72 - age));
  }
  return { vpwRate, rmdDivisor, ...inputs.strategy };
}

/**
 * What a starting spending level asks of the portfolio each year, before any
 * strategy adjusts it. Guaranteed income often starts some years into
 * retirement, so the early draws include a "bridge" on top of the steady draw
 * needed once all of that income is running. Rates are measured on the steady
 * draw; otherwise a strategy would mistake the end of the bridge for a windfall.
 */
function planFor(startSpend, env, n) {
  if (env.pace) env.pace.asProjected();
  const draw = new Float64Array(n);
  let steady = Infinity;
  for (let y = 0; y < n; y++) {
    draw[y] = env.drawFor(y, startSpend);
    if (draw[y] < steady) steady = draw[y];
  }
  const bridge = new Float64Array(n);
  for (let y = 0; y < n; y++) bridge[y] = draw[y] - steady;
  return { startSpend, draw, bridge, steady };
}

/**
 * Runs one strategy down one path. Withdrawals are taken at the start of the
 * year and the remainder earns that year's return. Returns the lowest year of
 * after-tax spending, lifetime spending, and the ending balance.
 *
 * `record.action` notes when a rule fired: for guardrails -1 is a cut and 1 a
 * raise; for the bucket method 1 is a refill of the cash reserve and -1 a year
 * in which the refill was skipped because the market fell.
 */
function runPath(key, plan, start, returns, env, tables, n, record) {
  const { startSpend } = plan;
  let balance = start;
  let target = startSpend;
  let previousSpend = startSpend;
  let lowest = Infinity;
  let total = 0;
  // True once savings could not pay a year's draw and spending fell short of
  // what the method meant to spend that year.
  let ranOut = false;

  const startRate = start > 0 ? Math.min(1, plan.steady / start) : 0;
  const reserveYears = tables.bucketYears;
  // Home equity held back as a last resort (the home-equity comparison only).
  let lastResort = env.reserve || 0;
  let cash = key === 'bucket' ? Math.min(balance, reserveYears * plan.draw[0]) : 0;
  // Conversions that wait for the market: each year decides from this path's balance.
  const pace = env.pace || null;
  if (pace) pace.begin();

  for (let y = 0; y < n; y++) {
    // One-off money arrives (or a lump is paid out) before the year's withdrawal.
    if (env.inflow[y] !== 0) {
      balance = Math.max(0, balance + env.inflow[y]);
      if (cash > balance) cash = balance;
    }
    if (pace) pace.enter(y, balance);
    let draw;
    let action = 0;
    switch (key) {
      case 'guardrails':
        // Guyton-Klinger capital-preservation and prosperity rules on the
        // steady draw rate, with cuts floored at the plan's success floor.
        if (y > 0 && balance > 0 && startRate > 0) {
          const rate = Math.max(0, env.drawFor(y, target) - plan.bridge[y]) / balance;
          if (rate > startRate * (1 + tables.guardrailBand)) {
            const lowered = Math.max(target * (1 - tables.guardrailCut), startSpend * SPENDING_FLOOR);
            if (lowered < target) action = -1;
            target = lowered;
          } else if (rate < startRate * (1 - tables.guardrailBand) && tables.guardrailRaise > 0) {
            target *= 1 + tables.guardrailRaise;
            action = 1;
          }
        }
        draw = env.drawFor(y, target);
        break;
      case 'percent':
        // A fixed share of the balance, plus the bridge until income starts.
        draw = startRate * balance + plan.bridge[y];
        break;
      case 'vanguard': {
        // Target a fixed share of the portfolio (plus the bridge), but limit
        // year-over-year changes in spending to a ceiling and a floor.
        if (y > 0) {
          const desired = env.spendFor(y, startRate * balance + plan.bridge[y]);
          target = Math.min(previousSpend * (1 + tables.vanguardCeiling), Math.max(previousSpend * (1 - tables.vanguardFloor), desired));
        }
        draw = env.drawFor(y, target);
        break;
      }
      case 'vpw':
        draw = balance * tables.vpwRate[n - y];
        break;
      case 'rmd':
        draw = balance / tables.rmdDivisor[y];
        break;
      default: // fixed, bucket
        // The draw for a level of spending depends on this path's conversions when they wait for the market.
        draw = pace ? env.drawFor(y, startSpend) : plan.draw[y];
    }
    if (draw > balance && lastResort > 0) {
      // Savings cannot cover the year: the reserve is released, once.
      balance += lastResort;
      lastResort = 0;
    }
    let short = false;
    if (draw > balance) {
      short = draw - balance > Math.max(1, draw * RAN_OUT_TOLERANCE);
      draw = balance;
    }

    // `spend` is measured as a starting level, so a year in which the plan
    // spends less by design is not counted as a cut.
    const spend = env.spendFor(y, draw);
    if (short) ranOut = true;
    previousSpend = spend;
    if (spend < lowest) lowest = spend;
    total += spend * env.factor[y];

    const r = returns[y];
    if (key === 'bucket') {
      // Spend from a cash reserve that earns nothing above inflation; refill
      // it from the invested portion only after a non-negative year.
      const fromCash = Math.min(cash, draw);
      cash -= fromCash;
      let invested = (balance - cash - fromCash - (draw - fromCash)) * (1 + r);
      if (invested < 0) invested = 0;
      if (r >= 0) {
        const refill = Math.min(invested, Math.max(0, reserveYears * plan.draw[Math.min(y + 1, n - 1)] - cash));
        invested -= refill;
        cash += refill;
        if (refill > 0) action = 1;
      } else {
        action = -1;
      }
      balance = invested + cash;
    } else {
      balance = (balance - draw) * (1 + r);
    }
    if (balance < 0) balance = 0;

    if (record) {
      record.balance[y] = balance;
      record.spend[y] = spend * env.factor[y];
      record.draw[y] = draw;
      record.cash[y] = cash;
      record.action[y] = action;
    }
  }
  const converted = pace ? pace.share() : null;
  if (pace) pace.asProjected();
  // A reserve never called on is still there at the end.
  return { lowest, total, balance: balance + lastResort, converted, ranOut };
}

// One test for every method: savings never run out while the year still
// needed them, spending never below the floor and, when a legacy is set, at
// least that much left at the end. Running out counts even when guaranteed
// income alone stays above the floor: otherwise the most a plan could spend
// sticks at that income / SPENDING_FLOOR whatever is saved.
const succeeded = (lowest, startSpend, ending = Infinity, legacy = 0, ranOut = false) =>
  !ranOut && lowest >= startSpend * SPENDING_FLOOR - 0.5 && ending >= legacy - 0.5;

/**
 * Everything a strategy run needs. `envAt(level)` is the tax table for a plan
 * that spends `level`: the mix of accounts each year's withdrawal comes from
 * depends on how fast the plan is spending, so each level has its own.
 */
function makeContext(inputs, paths, tables, income = buildIncome(inputs)) {
  const cache = new Map();
  const envAt = (level) => {
    const id = Math.round(level * 100);
    let env = cache.get(id);
    if (!env) {
      const projection = projectAccounts(inputs, { spending: level, income });
      const paced = pacedConversions(inputs.conversionPace) && projection.totalConverted > 0 && !conversionParts(projection.fillToRate).ladder;
      env = paced
        ? buildPacedEnv(inputs, income, projection, level)
        : buildTaxEnv(inputs, income, withdrawalMix(projection, inputs));
      if (cache.size >= 8) cache.delete(cache.keys().next().value);
      cache.set(id, env);
    }
    return env;
  };
  return { inputs, income, paths, tables, n: inputs.retirementYears, envAt };
}

function successRate(key, startSpend, ctx, stopBelow = 0) {
  const { paths, tables, n } = ctx;
  const env = ctx.envAt(startSpend);
  const maxFailures = Math.floor(paths.count * (1 - stopBelow));
  const plan = planFor(startSpend, env, n);
  let failures = 0;
  for (let i = 0; i < paths.count; i++) {
    const { lowest, balance, ranOut } = runPath(key, plan, paths.start[i], paths.returns[i], env, tables, n, null);
    if (!succeeded(lowest, startSpend, balance, ctx.inputs.legacyTarget, ranOut)) {
      failures++;
      // Past this point the answer to "is it at least stopBelow?" is already no.
      if (stopBelow > 0 && failures > maxFailures) return 0;
    }
  }
  return 1 - failures / paths.count;
}

// Both measures at one spending level, in one pass: our test (`successRate`)
// and the share of markets in which the money never ran out, the usual test
// in other calculators (`neverRanOutRate`, which ignores the spending floor).
function successAndRunway(key, startSpend, ctx) {
  const { paths, tables, n } = ctx;
  const env = ctx.envAt(startSpend);
  const plan = planFor(startSpend, env, n);
  let held = 0;
  let lasted = 0;
  for (let i = 0; i < paths.count; i++) {
    const { lowest, balance, ranOut } = runPath(key, plan, paths.start[i], paths.returns[i], env, tables, n, null);
    if (succeeded(lowest, startSpend, balance, ctx.inputs.legacyTarget, ranOut)) held++;
    if (!ranOut) lasted++;
  }
  return { successRate: held / paths.count, neverRanOutRate: lasted / paths.count };
}

// Simulated markets whose conversions are replayed in the projection to measure a paced plan's tax.
const PACE_SAMPLE = 40;

/**
 * With conversions that wait for the market, at spending `level`: the share
 * of the planned dollars converted, averaged over the paths, and what that
 * costs and saves. A projection only ever sees the expected return, at which
 * every conversion is made, so the tax, health credits and ending wealth are
 * measured by replaying, in the projection, the years `PACE_SAMPLE` of the
 * simulated markets actually converted in (and how much), and averaging.
 * Returns null when the plan does not pace its conversions.
 */
function measurePacedConversions(level, ctx) {
  const { paths, tables, n, inputs, income } = ctx;
  const env = ctx.envAt(level);
  if (!env.pace) return null;
  const plan = planFor(level, env, n);
  let made = 0;
  const patterns = new Map();
  const every = Math.max(1, Math.floor(paths.count / PACE_SAMPLE));
  for (let i = 0; i < paths.count; i++) {
    const sampled = i % every === Math.floor(every / 2) && patterns.size < PACE_SAMPLE + 1 ? new Float64Array(n) : null;
    env.pace.track(sampled);
    made += runPath('fixed', plan, paths.start[i], paths.returns[i], env, tables, n, null).converted;
    if (sampled) {
      const id = Array.from(sampled, (value) => Math.round(value * 20)).join(',');
      const seen = patterns.get(id);
      if (seen) seen.count++; else patterns.set(id, { scale: sampled, count: 1 });
    }
  }
  env.pace.track(null);
  const keys = ['lifetimeTax', 'lifetimeStateTax', 'lifetimePenalty', 'lifetimeIrmaa', 'lifetimeHealthCredit', 'endingAfterTax', 'totalConverted'];
  const measured = Object.fromEntries(keys.map((key) => [key, 0]));
  let weight = 0;
  for (const { scale, count } of patterns.values()) {
    const projection = projectAccounts(inputs, { spending: level, income, conversionScale: scale });
    keys.forEach((key) => { measured[key] += count * projection[key]; });
    weight += count;
  }
  keys.forEach((key) => { measured[key] = weight ? measured[key] / weight : 0; });
  return { made: paths.count ? made / paths.count : 1, patterns: patterns.size, sampled: weight, ...measured };
}

/** Highest starting spending that meets the success target under the common test. */
function findSustainableSpending(key, ctx, ceiling, steps = 17) {
  const passes = (level) => successRate(key, level, ctx, SUCCESS_TARGET) >= SUCCESS_TARGET;
  let lo = 0;
  let hi = ceiling;
  // The first guess at a ceiling is too low for very short plans: raise it
  // until the test actually fails there.
  for (let i = 0; i < 12 && passes(hi); i++) {
    lo = hi;
    hi *= 2;
  }
  // Results are shown to the nearest $500; this narrows to a few dollars.
  for (let i = 0; i < steps; i++) {
    const mid = (lo + hi) / 2;
    if (passes(mid)) lo = mid; else hi = mid;
  }
  return lo;
}

/**
 * Three simulated markets to follow year by year: a weak one, a middle one
 * and a strong one. They are ranked by where a plain 4% withdrawal would end
 * up, which depends on the order of returns and not on the method being
 * shown, so every method is followed through the same three markets.
 */
function pickExamples(paths, n, reference) {
  const N = paths.count;
  if (N === 0) return [];
  const score = new Float64Array(N);
  const draw = 0.04 * reference;
  for (let i = 0; i < N; i++) {
    let balance = paths.start[i];
    for (let y = 0; y < n; y++) balance = (balance - draw) * (1 + paths.returns[i][y]);
    score[i] = balance;
  }
  const order = Array.from({ length: N }, (_, i) => i).sort((a, b) => score[a] - score[b]);
  const at = (p) => order[Math.min(N - 1, Math.floor(p * N))];
  return [{ id: 'weak', index: at(0.10) }, { id: 'middle', index: at(0.5) }, { id: 'strong', index: at(0.90) }];
}

function summariseStrategy(key, startSpend, ctx, examples = []) {
  const { inputs, income, paths, tables, n } = ctx;
  const env = ctx.envAt(startSpend);
  const N = paths.count;
  const balances = Array.from({ length: n }, () => new Float64Array(N));
  const spends = Array.from({ length: n }, () => new Float64Array(N));
  const draws = Array.from({ length: n }, () => new Float64Array(N));
  const lowest = new Float64Array(N);
  const totals = new Float64Array(N);
  const ending = new Float64Array(N);
  const record = {
    balance: new Float64Array(n), spend: new Float64Array(n), draw: new Float64Array(n),
    cash: new Float64Array(n), action: new Int8Array(n),
  };
  let successes = 0;
  let lasted = 0;
  let cuts = 0;
  let full = 0;
  const conversions = [];
  const plan = planFor(startSpend, env, n);
  const followed = [];

  for (let i = 0; i < N; i++) {
    const result = runPath(key, plan, paths.start[i], paths.returns[i], env, tables, n, record);
    lowest[i] = result.lowest;
    totals[i] = result.total;
    ending[i] = result.balance;
    const held = succeeded(result.lowest, startSpend, result.balance, inputs.legacyTarget, result.ranOut);
    if (held) successes++;
    if (!result.ranOut) lasted++;
    if (result.lowest < startSpend * 0.995) cuts++; else if (held) full++;
    if (result.converted != null) conversions.push(result.converted);
    for (let y = 0; y < n; y++) {
      balances[y][i] = record.balance[y];
      spends[y][i] = record.spend[y];
      draws[y][i] = record.draw[y];
    }
    examples.filter((example) => example.index === i).forEach((example) => {
      followed.push({
        id: example.id,
        startBalance: paths.start[i],
        rows: Array.from({ length: n }, (_, y) => {
          const guaranteed = income.pension[y] + income.ss[y];
          return {
            age: inputs.startAge + y,
            marketReturn: paths.returns[i][y],
            guaranteedIncome: guaranteed,
            withdrawal: record.draw[y],
            spending: record.spend[y],
            tax: Math.max(0, guaranteed + record.draw[y] - record.spend[y] - income.fixed[y]),
            balance: record.balance[y],
            cash: record.cash[y],
            action: record.action[y],
          };
        }),
      });
    });
  }

  const byYear = (columns, p) => columns.map((column) => percentile(column.sort(), p));
  return {
    key,
    label: LABELS[key],
    startSpending: startSpend,
    successRate: successes / N,
    // Share in which the money never ran out, whatever happened to spending (the usual test elsewhere).
    neverRanOutRate: lasted / N,
    // Share of simulations in which spending fell below its starting level at least once.
    cutShare: cuts / N,
    // Share in which every year's spending was met in full (and any amount to leave was left).
    fullRate: full / N,
    // With conversions that wait for the market: the share of the planned
    // conversion dollars actually made, on average and in one market in ten.
    conversionsMade: conversions.length ? conversions.reduce((sum, value) => sum + value, 0) / conversions.length : null,
    conversionsMadeLow: conversions.length ? percentile(conversions.sort(), 0.10) : null,
    lowYearSpendingP10: percentile(lowest.sort(), 0.10),
    medianLifetimeSpending: percentile(totals.sort(), 0.5),
    medianAverageSpending: n > 0 ? percentile(totals, 0.5) / n : 0,
    medianEndBalance: percentile(ending.sort(), 0.5),
    medianBalanceByYear: byYear(balances, 0.5),
    p10BalanceByYear: byYear(balances, 0.10),
    p90BalanceByYear: byYear(balances, 0.90),
    medianSpendingByYear: byYear(spends, 0.5),
    p10SpendingByYear: byYear(spends, 0.10),
    p90SpendingByYear: byYear(spends, 0.90),
    medianWithdrawalByYear: byYear(draws, 0.5),
    examples: examples.map((example) => followed.find((f) => f.id === example.id)).filter(Boolean),
  };
}

// ── Deterministic account-level projection ─────────────────────────────────
//
// One model behind the sources chart, the tax picture, the RMD table and the
// Roth comparison: three account types grown at the expected return, drawn on
// in the chosen order, RMDs and 72(t) payments enforced, tax and any
// early-withdrawal penalty paid out of the withdrawals themselves. The same
// projection tells the simulations which accounts each year's withdrawal
// comes from.

// Where a year's withdrawal comes from, in order, under "taxable first".
// Before 59 1/2, Roth money that is free of the penalty (contributions and
// seasoned conversions) comes ahead of a penalised traditional withdrawal, and
// Roth growth, which would be taxed and penalised, is the last resort.
const drawSequence = (early) => (early
  ? ['taxable', 'rothFree', 'traditional', 'rothLocked']
  : ['taxable', 'traditional', 'rothFree']);

/**
 * `rothFree` is how much of the Roth balance can come out without tax or
 * penalty this year; `exempt` is how much traditional money is free of the
 * penalty (a 401(k) under the rule of 55). Both only matter before 59 1/2.
 */
function solveYear({ need, pension, ss, balances, basis, rmd, sepp, conversion, order, filingStatus, age, spouseAge, rothFree = Infinity, exempt = 0, stateRate = 0, state = '', localRate = 0, pensionOnly = 0, iraShare = 0, incomeCap = null, prices = 1, calendarYear = null, taxFactor = 1 }) {
  const early = age < PENALTY_FREE_AGE;
  const total = balances.traditional + balances.roth + balances.taxable;
  const available = Math.max(0, balances.traditional - conversion);
  const free = early ? Math.min(balances.roth, Math.max(0, rothFree)) : balances.roth;
  const locked = balances.roth - free;
  // Money that can be reached without touching Roth growth early.
  const liquid = available + free + balances.taxable;
  const seppPaid = Math.min(available, sepp);
  const forced = Math.min(available, rmd + sepp);
  const exemptRoom = early ? Math.min(Math.max(0, exempt), available) : 0;
  const gainsFraction = balances.taxable > 0 ? clamp(1 - basis / balances.taxable, 0, 1) : 0;
  const sequence = drawSequence(early);
  const room = { traditional: available, rothFree: free, rothLocked: locked, taxable: balances.taxable };

  const evaluate = (draw) => {
    const from = { traditional: forced, rothFree: 0, rothLocked: 0, taxable: 0 };
    if (order !== 'proportional') {
      let rest = Math.max(0, draw - forced);
      // The income a premium credit is measured on is ordinary income, gains
      // and all of Social Security. While that can be kept under the cap,
      // taxable holdings are sold only as far as their gains fit, and Roth
      // money that comes out free covers the rest.
      const fixedIncome = pension + ss + forced + conversion;
      if (incomeCap != null && fixedIncome < incomeCap) {
        const fits = gainsFraction > 0 ? (incomeCap - fixedIncome) / gainsFraction : Infinity;
        for (const [type, limit] of [['taxable', Math.min(room.taxable, fits)], ['rothFree', room.rothFree]]) {
          const take = Math.max(0, Math.min(limit - from[type], rest));
          from[type] += take;
          rest -= take;
        }
      }
      for (const type of sequence) {
        const take = Math.min(room[type] - from[type], rest);
        from[type] += take;
        rest -= take;
      }
    } else {
      const share = liquid > 0 ? Math.min(draw, liquid) / liquid : 0;
      from.traditional = Math.max(forced, available * share);
      from.rothFree = free * share;
      from.taxable = balances.taxable * share;
      from.rothLocked = Math.min(locked, Math.max(0, draw - liquid));
    }
    const exemptUsed = Math.min(exemptRoom, Math.max(0, from.traditional - seppPaid));
    const penaltyBase = early ? Math.max(0, from.traditional - seppPaid - exemptUsed) + from.rothLocked : 0;
    const penalty = EARLY_WITHDRAWAL_PENALTY * penaltyBase;
    const gains = gainsFraction * from.taxable;
    const result = federalTax({
      // Roth growth taken early is ordinary income.
      ordinary: pension + from.traditional + conversion + from.rothLocked,
      ss,
      gains,
      filingStatus, age, spouseAge, stateRate, state, localRate, prices, year: calendarYear, taxFactor,
      pensionIncome: pensionOnly, planIncome: from.traditional + conversion, iraShare,
    });
    const fromRoth = from.rothFree + from.rothLocked;
    return {
      fromTraditional: from.traditional, fromRoth, fromRothFree: from.rothFree, fromRothLocked: from.rothLocked, fromTaxable: from.taxable,
      ...result, penalty, penaltyBase, exemptUsed, gains, gainsFraction, seppPaid,
      spendable: pension + ss + from.traditional + fromRoth + from.taxable - result.tax - penalty,
    };
  };

  let lo = 0;
  let hi = liquid + locked;
  let outcome = evaluate(0);
  if (outcome.spendable < need && total > 0) {
    let below = outcome.spendable - need;
    outcome = evaluate(hi);
    let above = outcome.spendable - need;
    if (above >= 0) {
      // What a withdrawal leaves to spend rises almost in a straight line, so
      // aiming where the line crosses the need gets there in a few steps
      // (halving the stale end's weight when one side stops moving). The
      // answer is always the upper end: the smallest withdrawal found that
      // covers the need.
      let side = 0;
      for (let i = 0; i < 40 && hi - lo > 0.5 && above > 0.25; i++) {
        let mid = lo + (hi - lo) * (-below) / (above - below);
        if (!(mid > lo && mid < hi)) mid = (lo + hi) / 2;
        const next = evaluate(mid);
        const gap = next.spendable - need;
        if (gap >= 0) {
          hi = mid;
          above = gap;
          outcome = next;
          if (side === 1) below /= 2;
          side = 1;
        } else {
          lo = mid;
          below = gap;
          if (side === -1) above /= 2;
          side = -1;
        }
      }
    }
  }
  return outcome;
}

/**
 * The largest 72(t) payment the fixed amortisation method allows on a
 * balance, and the schedule the plan uses. The payment is a fixed dollar
 * amount, so its buying power falls with inflation each year it runs.
 */
// A penalty total below this counts as none when a 72(t) payment is sized.
const SEPP_FIT_TOLERANCE = 100;

/**
 * The smallest 72(t) payment that leaves the plan with no early-withdrawal
 * penalty: what it would otherwise take from traditional accounts (or from
 * Roth growth) before 59 1/2, once taxable money, Roth contributions and
 * anything else already free of the penalty have been used in the order the
 * plan draws them. The payment starts at retirement and is a fixed number of
 * dollars. Zero when the plan needs none; the largest allowed when even that
 * leaves a penalty.
 */
function fittedSeppPayment(inputs, options) {
  const earlyYears = Math.min(inputs.retirementYears, PENALTY_FREE_AGE - inputs.startAge);
  if (earlyYears <= 0) return 0;
  // Only the years before 59 1/2 can carry the penalty, so only they are projected.
  const penaltyWith = (amount) => {
    const projection = projectAccounts({ ...inputs, sepp: { mode: amount > 0 ? 'custom' : 'none', amount } }, { ...options, years: earlyYears });
    return { penalty: projection.lifetimePenalty, max: projection.sepp.max };
  };
  const none = penaltyWith(0);
  if (none.penalty < SEPP_FIT_TOLERANCE || !(none.max > 0)) return 0;
  if (penaltyWith(none.max).penalty >= SEPP_FIT_TOLERANCE) return none.max;
  let lo = 0;
  let hi = none.max;
  for (let i = 0; i < 9; i++) {
    const mid = (lo + hi) / 2;
    if (penaltyWith(mid).penalty < SEPP_FIT_TOLERANCE) hi = mid; else lo = mid;
  }
  return Math.min(none.max, Math.ceil(hi / 100) * 100);
}

/**
 * True when a projection takes the largest 72(t) payment its balance allows
 * and still pays an early-withdrawal penalty: the need is more than the cap.
 */
export const seppFallsShort = (projection) => projection.sepp.annual > 0
  && projection.sepp.annual >= projection.sepp.max - 1
  && projection.lifetimePenalty >= SEPP_FIT_TOLERANCE;

export function seppSchedule(inputs, traditionalAtRetirement) {
  const age = inputs.startAge;
  const lifeYears = SINGLE_LIFE_TABLE[age] ?? Math.max(1, SINGLE_LIFE_TABLE[20] + (20 - age) * 0.96);
  const i = SEPP_INTEREST_RATE;
  const max = age < PENALTY_FREE_AGE ? traditionalAtRetirement * i / (1 - Math.pow(1 + i, -lifeYears)) : 0;
  const mode = inputs.sepp?.mode || 'none';
  const annual = mode === 'max' ? max : mode === 'custom' ? Math.min(max, inputs.sepp.amount) : 0;
  const years = annual > 0 ? Math.max(SEPP_MIN_YEARS, PENALTY_FREE_AGE - age) : 0;
  // The IRA balance that produces this payment under the fixed amortisation
  // method: all of the traditional money for the largest payment, and for a
  // smaller one the part that would be set aside in an account of its own.
  const balanceUsed = annual > 0 ? annual * (1 - Math.pow(1 + i, -lifeYears)) / i : 0;
  return { annual, max, years, endAge: annual > 0 ? age + years - 1 : null, rate: i, lifeYears, balanceUsed };
}

/**
 * @param {object} options
 *   spending    after-tax spending to deliver (defaults to the plan's target)
 *   fillToRate  convert traditional money to Roth each year between retirement
 *               and the RMD age, up to the top of this bracket (null for none)
 *   order       withdrawal order
 *   income      guaranteed income, when it differs from the plan's own
 */
export function projectAccounts(inputs, options = {}) {
  const { filingStatus, savingsShares: shares, inflation } = inputs;
  const spending = options.spending ?? inputs.spending;
  const fillToRate = options.fillToRate !== undefined ? options.fillToRate : inputs.rothConversion;
  const order = options.order ?? inputs.withdrawalOrder;
  const income = options.income ?? buildIncome(inputs);
  const brackets = FEDERAL_TAX_BRACKETS[filingStatus] || FEDERAL_TAX_BRACKETS.married_filing_jointly;
  // Household taxable income that fills the bracket: two returns hold twice as much.
  const returnsFiled = filingStatus === 'married_filing_separately' ? 2 : 1;
  const policy = conversionParts(fillToRate);
  const ladder = policy.ladder;
  const bracketTop = policy.rate != null ? (brackets.find((b) => b.rate === policy.rate)?.max ?? null) : null;
  const fillCeiling = bracketTop != null ? bracketTop * returnsFiled : null;
  // A phased policy: one bracket until pensions or Social Security start, another (or none) from then on.
  const lateTop = policy.phased && policy.lateRate != null ? (brackets.find((b) => b.rate === policy.lateRate)?.max ?? null) : null;
  const lateCeiling = lateTop != null ? lateTop * returnsFiled : null;
  let incomeStartsAt = income.ss.findIndex((benefit, y) => benefit + income.parts.pension[y] > 0);
  if (incomeStartsAt < 0) incomeStartsAt = inputs.retirementYears;
  const scale = options.conversionScale || null;
  // Whether a year's income sets a Medicare surcharge: someone is enrolled two
  // years on (or, in the first two years, that year itself).
  const enrolledAt = (k) => {
    if (k >= inputs.retirementYears) return false;
    const spouseAt = inputs.spouseAge != null && income.spousePresent[k] ? inputs.spouseAge + inputs.yearsToRetirement + k : null;
    return annualIrmaa(Infinity, income.filing[k], inputs.startAge + k, spouseAt) > 0;
  };
  const setsSurcharge = (y) => enrolledAt(y + IRMAA_LOOKBACK_YEARS) || (y < IRMAA_LOOKBACK_YEARS && enrolledAt(y));

  const balances = { ...inputs.accounts };
  delete balances.total;
  // Cost basis of the taxable account in today's dollars. It is a fixed
  // number of dollars, so inflation shrinks it against the balance each year.
  let basis = inputs.taxableBasis;
  // Roth contributions, tracked the same way: fixed dollars that new savings add to.
  let rothBasis = inputs.rothBasis ?? balances.roth * DEFAULT_ROTH_BASIS_FRACTION;
  const before = lumpsBefore(inputs);
  for (let y = 0; y < inputs.yearsToRetirement; y++) {
    const saved = inputs.savings[y];
    const r = returnAt(inputs, y);
    for (const type of ['traditional', 'roth', 'taxable']) {
      balances[type] = balances[type] * (1 + r) + saved * shares[type];
    }
    basis = basis / (1 + inflation) + saved * shares.taxable;
    rothBasis = rothBasis / (1 + inflation) + saved * shares.roth;
    // One-off money lands in the taxable account; a one-off cost comes out of
    // every account in proportion.
    const lump = before.taxable[y];
    if (lump > 0) {
      balances.taxable += lump;
      basis += lump;
    } else if (lump < 0) {
      const held = balances.traditional + balances.roth + balances.taxable;
      const keep = held > 0 ? Math.max(0, 1 + lump / held) : 0;
      for (const type of ['traditional', 'roth', 'taxable']) balances[type] *= keep;
      basis *= keep;
      rothBasis *= keep;
    }
    balances.traditional = Math.max(0, balances.traditional + before.traditional[y]);
  }
  rothBasis = Math.min(rothBasis, balances.roth);
  const atRetirement = { ...balances, taxableBasis: Math.min(basis, balances.taxable), rothBasis };
  // A payment sized to the need is worked out first, from the plan without one.
  const sepp = seppSchedule(inputs.sepp?.mode === 'fit'
    ? { ...inputs, sepp: { mode: 'custom', amount: fittedSeppPayment(inputs, options) } }
    : inputs, balances.traditional);
  // Conversions made in retirement, each usable without penalty once seasoned.
  const layers = [];
  let exempt = balances.traditional * (inputs.penaltyFreeTraditionalShare || 0);
  const seasoned = (y) => layers.reduce((sum, layer) => sum + (y - layer.year >= CONVERSION_SEASONING_YEARS ? layer.amount : 0), 0);
  const needAfterIncome = (y) => Math.max(0, spending * income.factor[y] + income.fixed[y] - income.pension[y] - income.ss[y]);

  const rows = [];
  let lifetimeTax = 0;
  let lifetimeStateTax = 0;
  let lifetimeIrmaa = 0;
  let lifetimePenalty = 0;
  let lifetimeHealthCredit = 0;
  let totalConverted = 0;

  // `options.years` stops the projection early, for a caller that only needs the first years.
  const lastYear = Math.min(inputs.retirementYears, options.years ?? inputs.retirementYears);
  for (let y = 0; y < lastYear; y++) {
    const age = inputs.startAge + y;
    const r = returnAt(inputs, inputs.yearsToRetirement + y);
    const filing = income.filing[y];
    const spouseAge = inputs.spouseAge != null && income.spousePresent[y] ? inputs.spouseAge + inputs.yearsToRetirement + y : null;
    if (income.inflow[y] > 0) {
      balances.taxable += income.inflow[y];
      basis += income.inflow[y];
    }
    if (income.inflowTraditional[y] !== 0) balances.traditional = Math.max(0, balances.traditional + income.inflowTraditional[y]);
    const start = { ...balances };
    basis = Math.min(basis, balances.taxable);
    const startBasis = basis;
    const rmd = age >= inputs.rmdAge && balances.traditional > 0
      ? balances.traditional / (RMD_TABLE[age] || 2)
      : 0;
    const seppDue = y < sepp.years ? sepp.annual / Math.pow(1 + inflation, y) : 0;
    const early = age < PENALTY_FREE_AGE;
    const rothAccessible = early ? Math.min(balances.roth, rothBasis + seasoned(y)) : balances.roth;
    const exemptStart = early ? Math.min(exempt, balances.traditional) : 0;
    const lifestyle = spending * income.factor[y];
    const need = lifestyle + income.fixed[y];
    const year = {
      need, pension: income.pension[y], ss: income.ss[y], balances, basis, rmd, sepp: seppDue, order, filingStatus: filing, age, spouseAge,
      rothFree: rothAccessible, exempt: exemptStart, stateRate: inputs.stateTaxRate,
      state: inputs.state, localRate: inputs.localTaxRate, pensionOnly: income.parts.pension[y], iraShare: inputs.iraShare, prices: priceLevel(inputs, y), calendarYear: taxYear(inputs, y), taxFactor: inputs.taxFactor,
      // For the credit-aware order: the income to stay under while a premium credit is in reach.
      incomeCap: order === 'creditAware' && income.healthBenchmark[y] > 0 && income.parts.health[y] > 0
        ? acaCliffIncome(income.household[y]) - CREDIT_LIMIT_MARGIN : null,
    };
    const creditPossible = income.healthBenchmark[y] > 0 && income.parts.health[y] > 0;

    // Medicare sets a year's premium from the tax return of two years
    // earlier, so the surcharge paid this year follows from income already in
    // the projection. In the first two years of the plan that return is from
    // before retirement and is not known here: the year's own income is used,
    // which is what Medicare does on request after someone stops work.
    const earlier = y >= IRMAA_LOOKBACK_YEARS ? rows[y - IRMAA_LOOKBACK_YEARS] : null;
    const knownCharge = earlier ? annualIrmaa(earlier.agi, filing, age, spouseAge) : 0;

    // One year, for a given conversion. A Medicare premium surcharge
    // is a real cost, so it is paid from the accounts
    // like the tax (one refinement pass is enough: the tiers are wide). A
    // health credit works the other way round: it lowers what has to be
    // withdrawn, which lowers income, which raises the credit. Starting from
    // the largest credit possible and working down settles on the answer.
    const solveWith = (conversion) => {
      let credit = 0;
      if (creditPossible) {
        credit = Math.min(income.parts.health[y], income.healthBenchmark[y]);
        for (let i = 0; i < 8; i++) {
          const next = creditFor(inputs, income, y, solveYear({ ...year, need: need - credit + knownCharge, conversion }));
          if (Math.abs(next - credit) < 1) { credit = Math.min(credit, next); break; }
          credit = Math.min(credit, next);
        }
      }
      let result = solveYear({ ...year, need: need - credit + knownCharge, conversion });
      // Paying for the year can itself change the credit (income moved across
      // the limit): the year is settled with the credit its own income gives.
      if (creditPossible) {
        const actual = creditFor(inputs, income, y, result);
        if (actual < credit - 1) {
          credit = actual;
          result = solveYear({ ...year, need: need - credit + knownCharge, conversion });
        }
      }
      if (earlier) return { ...result, surcharge: knownCharge, healthCredit: credit };
      // Paying the surcharge raises the withdrawal, which can reach the next
      // tier: repeat until the surcharge paid is the one the year's income gives.
      let charge = annualIrmaa(result.agi, filing, age, spouseAge);
      for (let i = 0; i < 4 && charge > 0; i++) {
        result = solveYear({ ...year, need: need - credit + charge, conversion });
        const next = annualIrmaa(result.agi, filing, age, spouseAge);
        if (next <= charge) break;
        charge = next;
      }
      return { ...result, surcharge: charge, healthCredit: credit };
    };

    let conversion = 0;
    let out = solveWith(0);
    // The year's ceiling. A credit policy holds income (the figure the premium
    // credit is measured on) just under the level where the credit stops, in a
    // year someone is on marketplace coverage. If the year's income is over that
    // level before any conversion, the credit is gone whatever is converted,
    // so the year is treated like any other: the bracket, if one is named.
    const creditIncome = (result) => result.agi + income.ss[y] - result.taxableSS;
    const creditLimit = policy.credit && creditPossible ? acaCliffIncome(income.household[y]) - CREDIT_LIMIT_MARGIN : null;
    const holdCredit = creditLimit != null && creditIncome(out) <= creditLimit;
    const ceiling = policy.phased && y >= incomeStartsAt ? lateCeiling : fillCeiling;
    // Held under the Medicare surcharge: in a year whose income sets
    // someone's surcharge (two years on, or this year at the start of the
    // plan), the conversion stops short of the next tier.
    const medicareCap = inputs.conversionIrmaa && !holdCredit && setsSurcharge(y) ? nextIrmaaTier(out.agi, filing) : null;
    const medicareLimit = medicareCap != null ? medicareCap - CONVERSION_LIMIT_MARGIN : null;
    // How far a year is over its ceiling (negative: inside it).
    const bracketOver = ceiling != null && Number.isFinite(ceiling) ? (result) => result.ordinaryTaxable - ceiling : null;
    const over = holdCredit
      ? (result) => creditIncome(result) - creditLimit
      : bracketOver && medicareLimit != null ? (result) => Math.max(bracketOver(result), result.agi - medicareLimit) : bracketOver;
    const headroom = holdCredit ? creditLimit - creditIncome(out)
      : ceiling != null ? Math.min(ceiling - out.ordinaryTaxable, medicareLimit != null ? medicareLimit - out.agi : Infinity) : 0;
    if (over && age < inputs.rmdAge && balances.traditional > 0) {
      // Converting raises the tax bill, which is paid by larger withdrawals,
      // which are themselves income. Find the largest conversion that still
      // leaves the year inside its ceiling. Income rises almost in a straight
      // line with the amount converted, so aiming where that line meets the
      // ceiling gets there in a few steps.
      let lo = 0;
      // Converting can lower the year's traditional withdrawal and uses any deduction left over, so the
      // room is looked for a little beyond the bracket's remaining space; the search keeps it inside.
      let hi = Math.max(0, Math.min(balances.traditional - out.fromTraditional, headroom > 0 ? headroom + out.fromTraditional + out.deduction : 0));
      let under = over(out);
      let above = hi > 0 ? over(solveWith(hi)) : 1;
      if (hi > 0 && above <= 0) {
        lo = hi;
      } else if (hi > 0 && under <= 0) {
        let side = 0;
        for (let i = 0; i < 30 && hi - lo > 20; i++) {
          let mid = lo + (hi - lo) * (-under) / (above - under);
          if (!(mid > lo && mid < hi)) mid = (lo + hi) / 2;
          const gap = over(solveWith(mid));
          if (gap <= 0) {
            lo = mid;
            under = gap;
            if (gap > -20) break;
            if (side === -1) above /= 2;
            side = -1;
          } else {
            hi = mid;
            above = gap;
            if (side === 1) under /= 2;
            side = 1;
          }
        }
      }
      conversion = lo;
      // Replaying a simulated market: the year converts the share of this that the market did.
      if (scale) conversion = Math.min(conversion * scale[y], Math.max(0, balances.traditional - out.fromTraditional));
      if (conversion > 0) out = solveWith(conversion);
    }
    // What limited the year's conversion: a cliff (the credit, a surcharge tier) or a bracket.
    const conversionLimit = !over ? null : holdCredit ? 'credit'
      : medicareLimit != null && conversion > 1 && out.agi > medicareLimit - 50 && out.ordinaryTaxable < ceiling - 50 ? 'medicare' : 'bracket';
    if (ladder && age + CONVERSION_SEASONING_YEARS < PENALTY_FREE_AGE && y + CONVERSION_SEASONING_YEARS < inputs.retirementYears) {
      // The years still to get through before 59 1/2, against the money that
      // can already reach them without a penalty. Convert only what is short,
      // and no more than the year it will season in will need.
      let bridge = 0;
      for (let k = y + 1; k < inputs.retirementYears && inputs.startAge + k < PENALTY_FREE_AGE; k++) bridge += needAfterIncome(k);
      const unseasoned = layers.reduce((sum, layer) => sum + (y - layer.year < CONVERSION_SEASONING_YEARS ? layer.amount : 0), 0);
      const inReach = Math.max(0, balances.taxable - out.fromTaxable) + Math.max(0, rothAccessible - out.fromRothFree) + unseasoned;
      // A conversion is a fixed number of dollars, so by the time it seasons
      // inflation has shrunk what it buys: convert enough to allow for that.
      const erosion = Math.pow(1 + inflation, CONVERSION_SEASONING_YEARS);
      const wanted = Math.min(needAfterIncome(y + CONVERSION_SEASONING_YEARS), Math.max(0, bridge - inReach)) * erosion;
      conversion = Math.min(wanted, Math.max(0, balances.traditional - out.fromTraditional));
      if (conversion > 0) out = solveWith(conversion);
    }
    const surcharge = out.surcharge;
    const healthCredit = out.healthCredit;
    // What the year has to deliver after the credit.
    const due = need - healthCredit;

    // Anything left after spending (a forced distribution, or income above
    // the target) is reinvested in the taxable account at full basis.
    const surplus = Math.max(0, out.spendable - due - surcharge);
    const basisSold = start.taxable > 0 ? basis * Math.min(1, out.fromTaxable / start.taxable) : 0;
    basis = (basis - basisSold + surplus) / (1 + inflation);
    // Roth money comes out in the order the tax rules set: contributions,
    // then conversions oldest first, then growth.
    let rothFreeUsed = out.fromRothFree;
    const fromContributions = Math.min(rothBasis, rothFreeUsed);
    rothBasis -= fromContributions;
    rothFreeUsed -= fromContributions;
    for (const layer of layers) {
      if (rothFreeUsed <= 0) break;
      if (y - layer.year < CONVERSION_SEASONING_YEARS) continue;
      const take = Math.min(layer.amount, rothFreeUsed);
      layer.amount -= take;
      rothFreeUsed -= take;
    }
    if (conversion > 0) layers.push({ year: y, amount: conversion });
    rothBasis /= (1 + inflation);
    layers.forEach((layer) => { layer.amount /= (1 + inflation); });
    exempt = Math.max(0, Math.min(exempt - out.exemptUsed, balances.traditional - out.fromTraditional - conversion)) * (1 + r);

    balances.traditional = (balances.traditional - out.fromTraditional - conversion) * (1 + r);
    balances.roth = (balances.roth - out.fromRoth + conversion) * (1 + r);
    balances.taxable = (balances.taxable - out.fromTaxable + surplus) * (1 + r);
    for (const type of ['traditional', 'roth', 'taxable']) balances[type] = Math.max(0, balances[type]);

    lifetimeTax += out.tax - out.stateTax;
    lifetimeStateTax += out.stateTax;
    lifetimeIrmaa += surcharge;
    lifetimePenalty += out.penalty;
    lifetimeHealthCredit += healthCredit;
    totalConverted += conversion;

    rows.push({
      age,
      startTraditional: start.traditional,
      startRoth: start.roth,
      startTaxable: start.taxable,
      taxableBasis: startBasis,
      gainsFraction: out.gainsFraction,
      pension: income.pension[y],
      workIncome: income.parts.work[y],
      taxFreeIncome: income.parts.taxFree[y],
      moneyIn: income.inflow[y] + income.inflowTraditional[y],
      socialSecurity: income.ss[y],
      taxableSocialSecurity: out.taxableSS,
      rmd,
      seppPayment: out.seppPaid,
      traditionalWithdrawal: out.fromTraditional,
      rothWithdrawal: out.fromRoth,
      // Before 59 1/2: Roth money that could come out freely at the start of
      // the year, and any growth that had to be taken with tax and penalty.
      rothAccessible,
      rothEarningsWithdrawal: out.fromRothLocked,
      // Traditional money taken without the penalty under the rule of 55.
      ruleOf55Withdrawal: out.exemptUsed,
      penaltyFreeTraditional: exemptStart,
      penalizedWithdrawal: out.penaltyBase,
      taxableWithdrawal: out.fromTaxable,
      capitalGains: out.gains,
      conversion,
      conversionLimit,
      federalTax: out.tax - out.stateTax,
      stateTax: out.stateTax,
      // Health coverage before Medicare (after any credit) and Medicare premiums.
      healthCost: income.parts.health[y] - healthCredit + income.parts.medicare[y],
      healthCredit,
      // The pieces: the full price before any credit, the benchmark plan the
      // credit is measured on, who is covered, and the income test.
      healthFullCost: income.parts.health[y],
      healthBenchmark: income.healthBenchmark[y],
      healthCovered: income.healthCovered[y],
      medicarePremiums: income.parts.medicare[y],
      household: income.household[y],
      creditIncome: out.agi + income.ss[y] - out.taxableSS,
      creditLimit: income.healthBenchmark[y] > 0 ? acaCliffIncome(income.household[y]) : null,
      // A mortgage, care, one-off bills and payroll tax on pay.
      otherCosts: income.parts.mortgage[y] + income.parts.care[y] + income.parts.oneTime[y] + income.parts.payroll[y],
      // What is planned to be spent that year: the level, less anything inside it that has stopped.
      lifestyleSpending: lifestyle - income.parts.relief[y],
      penalty: out.penalty,
      marginalRate: out.marginalRate,
      ordinaryTaxable: out.ordinaryTaxable,
      deduction: out.deduction,
      agi: out.agi,
      irmaa: surcharge,
      surplus,
      afterTaxSpending: Math.max(0, Math.min(out.spendable - surcharge - (due - lifestyle), lifestyle) - income.parts.relief[y]),
      shortfall: Math.max(0, due + surcharge - out.spendable),
    });
  }

  return {
    fillToRate,
    order,
    spending,
    sepp,
    atRetirement,
    rows,
    lifetimeTax,
    lifetimeStateTax,
    lifetimeIrmaa,
    lifetimePenalty,
    lifetimeHealthCredit,
    totalConverted,
    ending: { ...balances },
    // Traditional money still owes tax when it is eventually withdrawn.
    endingAfterTax: balances.roth + balances.taxable + balances.traditional * (1 - TRADITIONAL_EXIT_TAX),
  };
}

/**
 * For each year: the fixed amounts taxed regardless of the withdrawal (a
 * 72(t) payment, a Roth conversion), the share of the rest of the withdrawal
 * that comes from traditional and from taxable accounts (the remainder is
 * Roth), and the share of a taxable sale that is gain.
 */
export function withdrawalMix(projection, inputs) {
  const n = inputs.retirementYears;
  const mix = {
    traditional: new Float64Array(n), taxable: new Float64Array(n), gainsFraction: new Float64Array(n),
    sepp: new Float64Array(n), conversion: new Float64Array(n),
    // Share of the withdrawal that carries the early penalty, and the share
    // that is Roth growth taken early (taxed as income).
    penalized: new Float64Array(n), rothTaxed: new Float64Array(n),
  };
  let last = { traditional: inputs.shares.traditional, taxable: inputs.shares.taxable, penalized: 0, rothTaxed: 0 };
  projection.rows.forEach((row, y) => {
    // What was withdrawn on top of any 72(t) payment.
    const withdrawn = row.traditionalWithdrawal - row.seppPayment + row.rothWithdrawal + row.taxableWithdrawal;
    const held = row.startTraditional + row.startRoth + row.startTaxable;
    if (withdrawn > 1) {
      last = {
        traditional: (row.traditionalWithdrawal - row.seppPayment) / withdrawn,
        taxable: row.taxableWithdrawal / withdrawn,
        penalized: row.penalizedWithdrawal / withdrawn,
        rothTaxed: row.rothEarningsWithdrawal / withdrawn,
      };
    } else if (held > 1) {
      const early = row.age < PENALTY_FREE_AGE;
      // A traditional dollar is penalised early unless the rule of 55 covers it.
      const exposed = early && !(row.penaltyFreeTraditional > 1) ? 1 : 0;
      // Nothing was needed this year: use where the next dollar would come from.
      if (projection.order !== 'proportional') {
        const balance = { traditional: row.startTraditional, rothFree: row.rothAccessible, rothLocked: row.startRoth - row.rothAccessible, taxable: row.startTaxable };
        const first = drawSequence(early).find((type) => balance[type] > 1);
        last = {
          traditional: first === 'traditional' ? 1 : 0,
          taxable: first === 'taxable' ? 1 : 0,
          penalized: first === 'traditional' ? exposed : first === 'rothLocked' ? 1 : 0,
          rothTaxed: first === 'rothLocked' ? 1 : 0,
        };
      } else {
        const reach = row.startTraditional + row.rothAccessible + row.startTaxable;
        last = reach > 1
          ? { traditional: row.startTraditional / reach, taxable: row.startTaxable / reach, penalized: exposed * row.startTraditional / reach, rothTaxed: 0 }
          : { traditional: 0, taxable: 0, penalized: early ? 1 : 0, rothTaxed: early ? 1 : 0 };
      }
    }
    // Once the projection has run dry, keep the last mix it used.
    mix.traditional[y] = last.traditional;
    mix.taxable[y] = last.taxable;
    // The penalty ends at 59 1/2 whatever the last mix was.
    mix.penalized[y] = row.age < PENALTY_FREE_AGE ? last.penalized : 0;
    mix.rothTaxed[y] = row.age < PENALTY_FREE_AGE ? last.rothTaxed : 0;
    mix.gainsFraction[y] = row.gainsFraction;
    mix.sepp[y] = row.seppPayment;
    mix.conversion[y] = row.conversion;
  });
  return mix;
}

const shortfallAge = (projection) => projection.rows.find((row) => row.shortfall > 1)?.age ?? null;

/** Compares no conversions with filling each of the lower brackets. */
export function compareRothConversions(inputs, plan = projectAccounts(inputs)) {
  const baseline = plan.fillToRate == null ? plan : projectAccounts(inputs, { fillToRate: null });
  const windowStart = inputs.startAge;
  const windowEnd = inputs.rmdAge - 1;
  const hasWindow = inputs.accounts.traditional > 0 && windowEnd >= windowStart;
  const brackets = FEDERAL_TAX_BRACKETS[inputs.filingStatus] || FEDERAL_TAX_BRACKETS.married_filing_jointly;
  const options = hasWindow
    ? brackets.filter((b) => CONVERSION_RATES.includes(b.rate))
      .map((b) => (b.rate === plan.fillToRate ? plan : projectAccounts(inputs, { fillToRate: b.rate })))
      .filter((o) => o.totalConverted > 0)
      .concat((plan.fillToRate === LADDER || conversionParts(plan.fillToRate).credit || conversionParts(plan.fillToRate).phased) && plan.totalConverted > 0 ? [plan] : [])
    : [];

  const describe = (option) => {
    const converting = option.rows.filter((row) => row.conversion > 0);
    const charged = option.rows.filter((row) => row.irmaa > 0);
    return {
      fillToRate: option.fillToRate,
      selected: option.fillToRate === inputs.rothConversion,
      totalConverted: option.totalConverted,
      firstConversionAge: converting.length ? converting[0].age : null,
      lastConversionAge: converting.length ? converting[converting.length - 1].age : null,
      lifetimeTax: option.lifetimeTax,
      lifetimePenalty: option.lifetimePenalty,
      lifetimeIrmaa: option.lifetimeIrmaa,
      // Ages at which the year's income was high enough to raise Medicare premiums.
      surchargeFromAge: charged.length ? charged[0].age : null,
      surchargeToAge: charged.length ? charged[charged.length - 1].age : null,
      surchargeYears: charged.length,
      surchargeYearsWhileConverting: charged.filter((row) => row.conversion > 0).length,
      endingAfterTax: option.endingAfterTax,
      gain: option.endingAfterTax - baseline.endingAfterTax,
      shortfallYears: option.rows.filter((row) => row.shortfall > 1).length,
    };
  };

  // A ladder exists to reach money early, not to end with the most, so it is shown but not ranked.
  const viable = options.filter((o) => typeof o.fillToRate === 'number' && (o.rows.every((row) => row.shortfall <= 1) || baseline.rows.some((row) => row.shortfall > 1)));
  const best = viable.reduce((top, o) => (o.endingAfterTax > (top?.endingAfterTax ?? -Infinity) ? o : top), null);
  // Differences smaller than this are inside the model's own noise.
  const meaningful = best && best.endingAfterTax - baseline.endingAfterTax > 0.01 * Math.max(1, baseline.endingAfterTax);

  return {
    hasWindow,
    windowStart,
    windowEnd,
    baseline: describe(baseline),
    options: options.map(describe),
    best: meaningful ? { ...describe(best), rows: best.rows } : null,
  };
}

/** The same plan under each withdrawal order, at the expected return. */
function compareWithdrawalOrders(inputs, plan) {
  // The credit-aware order differs from taxable first only where the plan works out a premium credit.
  const creditInPlay = inputs.healthEstimate || inputs.acaBenchmark > 0;
  return WITHDRAWAL_ORDERS.filter((order) => order !== 'creditAware' || creditInPlay || order === plan.order).map((order) => {
    const option = order === plan.order ? plan : projectAccounts(inputs, { order });
    return {
      order,
      selected: order === plan.order,
      lifetimeTax: option.lifetimeTax,
      lifetimePenalty: option.lifetimePenalty,
      lifetimeIrmaa: option.lifetimeIrmaa,
      endingAfterTax: option.endingAfterTax,
      shortfallAge: shortfallAge(option),
    };
  });
}

// ── Reaching retirement money before 59 1/2 ────────────────────────────────

const EARLY_ACCESS_OPTIONS = [
  { id: 'none', sepp: 'none', conversion: null },
  { id: 'sepp', sepp: 'fit', conversion: null },
  { id: 'ladder', sepp: 'none', conversion: LADDER },
  { id: 'both', sepp: 'fit', conversion: LADDER },
];

/**
 * The same plan with each way of reaching traditional money before 59 1/2:
 * nothing special, 72(t) payments, a Roth conversion ladder, or both. Null for
 * a retirement that starts at or after the penalty-free age.
 */
export function compareEarlyAccess(inputs, plan = projectAccounts(inputs)) {
  if (!(inputs.startAge < PENALTY_FREE_AGE) || !(plan.atRetirement.traditional > 0)) return null;
  const ladderPossible = inputs.startAge + CONVERSION_SEASONING_YEARS < PENALTY_FREE_AGE;
  const matches = (option) => option.sepp === inputs.sepp.mode && option.conversion === inputs.rothConversion;
  const offered = EARLY_ACCESS_OPTIONS.filter((option) => ladderPossible || option.conversion == null);
  // The plan's own combination (a custom 72(t) amount, or conversions to a bracket) gets its own row.
  const list = offered.some(matches) ? offered : [{ id: 'plan', sepp: inputs.sepp.mode, conversion: inputs.rothConversion, custom: true }, ...offered];

  const options = list.map((option) => {
    const selected = option.custom || matches(option);
    const changes = { sepp: selected ? inputs.sepp : { mode: option.sepp, amount: 0 }, rothConversion: option.conversion };
    const projection = selected ? plan : projectAccounts({ ...inputs, ...changes }, { fillToRate: option.conversion });
    const early = projection.rows.filter((row) => row.age < PENALTY_FREE_AGE);
    return {
      id: option.id,
      selected,
      changes,
      seppAnnual: projection.sepp.annual,
      seppShort: seppFallsShort(projection),
      totalConverted: projection.totalConverted,
      lifetimePenalty: projection.lifetimePenalty,
      penaltyYears: early.filter((row) => row.penalty > 1).length,
      taxBeforePenaltyFreeAge: early.reduce((sum, row) => sum + row.federalTax, 0),
      lifetimeTax: projection.lifetimeTax,
      endingAfterTax: projection.endingAfterTax,
      shortfallAge: shortfallAge(projection),
    };
  });

  const earlyRows = plan.rows.filter((row) => row.age < PENALTY_FREE_AGE);
  return {
    fromAge: inputs.startAge,
    toAge: PENALTY_FREE_AGE - 1,
    ladderPossible,
    ruleOf55: inputs.penaltyFreeTraditionalShare > 0,
    rothBasisEntered: inputs.rothBasisEntered,
    rothBasisAtRetirement: plan.atRetirement.rothBasis,
    penaltyAsPlanned: plan.lifetimePenalty,
    // What pays for each early year under the plan as entered.
    years: earlyRows.map((row) => ({
      age: row.age,
      taxable: row.taxableWithdrawal,
      rothFree: row.rothWithdrawal - row.rothEarningsWithdrawal,
      sepp: row.seppPayment,
      ruleOf55: row.ruleOf55Withdrawal,
      penalized: row.penalizedWithdrawal,
      converted: row.conversion,
      penalty: row.penalty,
      federalTax: row.federalTax,
    })),
    options,
  };
}

// ── Searching the withdrawal plans ─────────────────────────────────────────

// Differences smaller than this are inside the noise of the simulation.
export const SEARCH_NOISE = 500;
// Combinations within this many steps of the top count as supporting about as much.
const SEARCH_LEADER_STEPS = 2;

// 72(t) payments tried between none and the largest allowed, as shares of the largest.
export const SEPP_SEARCH_SHARES = [0.5];
// How many combinations go on from the projection to the simulated markets under each heading.
const SEARCH_FINALISTS = 6;
// The pace a converting combination is simulated at.
export const SEARCH_PACE = 'catchUp';

/**
 * A search of the withdrawal plan: every combination of withdrawal order, Roth
 * conversion policy and 72(t) payment size.
 *
 * It runs in two stages, because a projection takes milliseconds and a run
 * through the simulated markets takes much longer. First every combination is
 * projected at the expected return, which gives its lifetime tax, health
 * premium credits, penalties and ending wealth. Then the leaders on each of
 * those measures, every combination that converts nothing, and the plan as
 * entered go through the simulated markets, which gives the spending each
 * supports. A combination that leads on no measure in the projection is not
 * simulated; `tried` and `simulated` say how many of each there were.
 *
 * A search, not advice: it knows nothing the plan does not. `rows[0]` is the
 * top by spending supported; `current` is the plan as entered.
 */
export function searchWithdrawalPlans(inputs, { numSims = DEFAULT_NUM_SIMS, pace = SEARCH_PACE } = {}) {
  if (!(inputs.spending > 0) || !(inputs.accounts.total > 0 || inputs.yearsToRetirement > 0)) return null;
  const n = inputs.retirementYears;
  const income = buildIncome(inputs);
  const tables = strategyTables(inputs);
  const paths = buildPaths(inputs, numSims, simulatedMarkets(inputs, inputs.yearsToRetirement + n, numSims));
  const sortedStart = sortedCopy(paths.start);
  let guaranteed = 0;
  for (let y = 0; y < n; y++) guaranteed = Math.max(guaranteed, income.pension[y] + income.ss[y]);
  const ceiling = guaranteed + 0.3 * percentile(sortedStart, 0.90) + 1000;

  const base = projectAccounts(inputs, { income });
  const early = inputs.startAge < PENALTY_FREE_AGE && base.atRetirement.traditional > 0;
  const brackets = FEDERAL_TAX_BRACKETS[inputs.filingStatus] || FEDERAL_TAX_BRACKETS.married_filing_jointly;
  const canConvert = inputs.accounts.traditional > 0 && inputs.rmdAge - 1 >= inputs.startAge;
  const rates = canConvert ? brackets.filter((b) => CONVERSION_RATES.includes(b.rate)).map((b) => b.rate) : [];
  // Years in which a premium credit is possible: someone is on marketplace coverage and its full price is known.
  const creditYears = income.healthBenchmark.filter((premium, y) => premium > 0 && income.parts.health[y] > 0).length;
  const conversions = [null, ...rates];
  // Holding income at the credit limit in those years, with nothing or a bracket in the others.
  if (canConvert && creditYears > 0) conversions.push(creditCode(), ...rates.filter((rate) => rate >= 0.12).map((rate) => creditCode(rate)));
  // More in the years before pensions and Social Security start, less (or nothing) after.
  const incomeStarts = income.ss.findIndex((benefit, y) => benefit + income.parts.pension[y] > 0);
  if (canConvert && incomeStarts > 0) {
    rates.filter((rate) => rate >= 0.12).forEach((early) => {
      [null, ...rates.filter((rate) => rate >= 0.12 && rate < early)].forEach((late) => conversions.push(phaseCode(early, late)));
    });
  }
  const orders = WITHDRAWAL_ORDERS.filter((order) => order !== 'creditAware' || creditYears > 0);
  if (early && inputs.startAge + CONVERSION_SEASONING_YEARS < PENALTY_FREE_AGE) conversions.push(LADDER);
  const seppMax = early ? seppSchedule({ ...inputs, sepp: { mode: 'max', amount: 0 } }, base.atRetirement.traditional).max : 0;
  // No payment, one sized to the need, steps up to the largest, and the
  // largest. Any payment below the largest is what setting part of an IRA
  // aside in its own account, and taking the payments from that one, produces.
  const payments = [{ mode: 'none', amount: 0 }];
  if (seppMax > 0) {
    payments.push({ mode: 'fit', amount: 0 });
    SEPP_SEARCH_SHARES.forEach((share) => payments.push({ mode: 'custom', amount: Math.round(seppMax * share / 500) * 500 }));
    payments.push({ mode: 'max', amount: 0 });
  }
  const sameSepp = (a, b) => a.mode === b.mode && (a.mode !== 'custom' || Math.abs(a.amount - b.amount) < 1);
  const holdsMedicare = (c) => c.rothConversion != null && c.rothConversion !== LADDER && !!c.conversionIrmaa;
  const isCurrent = (c) => c.withdrawalOrder === inputs.withdrawalOrder && c.rothConversion === inputs.rothConversion && sameSepp(c.sepp, inputs.sepp)
    && holdsMedicare(c) === holdsMedicare(inputs);
  // Each conversion policy also held under the Medicare surcharge tiers, where someone reaches Medicare in the plan.
  const medicareYears = inputs.startAge + inputs.retirementYears > MEDICARE_AGE
    || (inputs.spouseAge != null && inputs.spouseAge + inputs.yearsToRetirement + inputs.retirementYears > MEDICARE_AGE);

  const candidates = [];
  for (const withdrawalOrder of orders) {
    for (const rothConversion of conversions) {
      const holds = rothConversion != null && rothConversion !== LADDER && medicareYears ? [false, true] : [false];
      for (const conversionIrmaa of holds) {
        for (const sepp of payments) candidates.push({ withdrawalOrder, rothConversion, conversionIrmaa, sepp });
      }
    }
  }
  // The plan as entered is always in the ranking, even with a 72(t) amount of
  // its own, and is looked at first, so that a combination identical to it is
  // the one dropped.
  if (!candidates.some(isCurrent)) {
    candidates.push({ withdrawalOrder: inputs.withdrawalOrder, rothConversion: inputs.rothConversion, conversionIrmaa: holdsMedicare(inputs), sepp: inputs.sepp });
  }
  candidates.sort((a, b) => Number(isCurrent(b)) - Number(isCurrent(a)));

  // Stage one: every combination in one projection at the expected return.
  const seen = new Map();
  const projected = [];
  for (const changes of candidates) {
    const changed = { ...inputs, ...changes };
    const projection = projectAccounts(changed, { income });
    // A conversion level that converts nothing, or a payment the balance
    // cannot make, is the same plan as one already tried.
    const signature = [changes.withdrawalOrder, Math.round(projection.totalConverted), Math.round(projection.sepp.annual), Math.round(projection.lifetimeTax), Math.round(projection.lifetimeHealthCredit)].join('|');
    const current = isCurrent(changes);
    // The plan as entered always keeps its own row: at another spending level
    // it can differ from a combination that looks the same at this one.
    if (seen.has(signature) && !current) continue;
    const row = {
      ...changes,
      changed,
      selected: current,
      seppAnnual: projection.sepp.annual,
      seppEndAge: projection.sepp.endAge,
      seppShort: seppFallsShort(projection),
      totalConverted: projection.totalConverted,
      lifetimeTax: projection.lifetimeTax,
      lifetimeStateTax: projection.lifetimeStateTax,
      lifetimePenalty: projection.lifetimePenalty,
      lifetimeIrmaa: projection.lifetimeIrmaa,
      healthCredits: projection.lifetimeHealthCredit,
      endingAfterTax: projection.endingAfterTax,
      shortfallAge: shortfallAge(projection),
    };
    row.totalTax = row.lifetimeTax + row.lifetimeStateTax + row.lifetimePenalty + row.lifetimeIrmaa;
    // What the choice costs in tax, less what it keeps in health credits.
    row.netTax = row.totalTax - row.healthCredits;
    seen.set(signature, row);
    projected.push(row);
  }

  // Stage two: the leaders on each measure, with every combination that
  // converts nothing and the plan as entered, through the simulated markets.
  const works = projected.filter((row) => row.shortfallAge == null);
  const pool = works.length ? works : projected;
  const finalists = new Set(projected.filter((row) => row.selected || row.totalConverted < 1));
  const lead = (compare) => [...pool].sort(compare).slice(0, SEARCH_FINALISTS).forEach((row) => finalists.add(row));
  lead((x, y) => x.netTax - y.netTax);
  lead((x, y) => y.endingAfterTax - x.endingAfterTax);
  lead((x, y) => y.healthCredits - x.healthCredits || x.netTax - y.netTax);

  // A combination that converts is simulated with its conversions waiting for
  // the market (made only in a year the portfolio is not far behind plan, and
  // made up later in a year that is): that is the pace that keeps most of the
  // saving without paying tax into a falling market. The plan as entered keeps
  // the pace it was entered with. A projection sees only the expected return,
  // at which every conversion is made, so a paced row's tax, credits and
  // ending wealth are measured again from the years the simulated markets
  // actually converted in (`measurePacedConversions`), at the spending entered.
  const paceable = (row) => row.totalConverted > 1 && !conversionParts(row.rothConversion).ladder;
  const rows = [];
  for (const finalist of finalists) {
    const conversionPace = !paceable(finalist) ? 'steady' : finalist.selected ? inputs.conversionPace : pace;
    const row = { ...finalist, conversionPace };
    const ctx = makeContext({ ...finalist.changed, conversionPace }, paths, tables, income);
    row.sustainableSpending = findSustainableSpending('fixed', ctx, ceiling);
    row.step = Math.round(row.sustainableSpending / SEARCH_NOISE);
    const measured = pacedConversions(conversionPace) ? measurePacedConversions(inputs.spending, ctx) : null;
    if (measured) {
      row.conversionsMade = measured.made;
      Object.assign(row, {
        lifetimeTax: measured.lifetimeTax,
        lifetimeStateTax: measured.lifetimeStateTax,
        lifetimePenalty: measured.lifetimePenalty,
        lifetimeIrmaa: measured.lifetimeIrmaa,
        healthCredits: measured.lifetimeHealthCredit,
        endingAfterTax: measured.endingAfterTax,
        totalConverted: measured.totalConverted,
      });
      row.totalTax = row.lifetimeTax + row.lifetimeStateTax + row.lifetimePenalty + row.lifetimeIrmaa;
      row.netTax = row.totalTax - row.healthCredits;
      row.measured = measured.sampled;
    }
    delete row.changed;
    rows.push(row);
  }
  finalists.forEach((row) => { delete row.changed; });
  // Most spending supported first, to the nearest step the simulation can
  // tell apart; within a step, the lower tax bill net of health credits.
  rows.sort((a, b) => b.step - a.step || a.netTax - b.netTax);
  const current = rows.find((row) => row.selected) || null;
  // Of the combinations that support about as much as the top one, the one that pays the least tax.
  const leaders = rows.filter((row) => row.step >= rows[0].step - SEARCH_LEADER_STEPS);
  const lowestTax = leaders.reduce((least, row) => (row.netTax < least.netTax ? row : least), leaders[0]);
  lowestTax.lowestTax = true;
  // The one that leaves the most at the expected return, whatever it supports in weak markets.
  const mostLeft = rows.reduce((most, row) => (row.endingAfterTax > most.endingAfterTax ? row : most), rows[0]);
  mostLeft.mostLeft = true;
  return {
    inputsKey: inputs.key,
    numSims: paths.count,
    tried: projected.length,
    simulated: rows.length,
    paceFloor: CONVERSION_PACE_FLOOR,
    pace,
    paceSample: PACE_SAMPLE,
    seppMax,
    early,
    creditYears,
    rows,
    current,
    lowestTax,
    mostLeft,
    leaderMargin: SEARCH_LEADER_STEPS * SEARCH_NOISE,
    // How much more the top of the ranking supports than the plan as entered.
    gain: current ? rows[0].sustainableSpending - current.sustainableSpending : 0,
  };
}

// ── Social Security claiming comparison ────────────────────────────────────

/**
 * Age by which a later, larger benefit has overtaken an earlier, smaller one
 * when every payment earns `rate` above inflation from the day it arrives.
 * At a rate of zero this is the familiar simple break-even. Null when the
 * later claim has not caught up by 110.
 */
export function claimBreakEvenAge(early, late, annualEarly, annualLate, rate) {
  let a = 0;
  let b = 0;
  for (let age = early; age < 110; age++) {
    a = a * (1 + rate) + annualEarly;
    if (age >= late) {
      b = b * (1 + rate) + annualLate;
      if (b >= a) return age + 1;
    }
  }
  return null;
}

function compareClaimAges(inputs, ctx, ceiling) {
  const primary = inputs.socialSecurity[0];
  if (!(primary.monthlyAtFRA > 0)) return null;
  // Already collecting: there is no claiming decision left to compare.
  if (primary.ownerAge > primary.claimAge) return { alreadyClaimed: true, scenarios: [], breakEvens: [] };
  // Benefits paid while still working are not carried into the plan, so only
  // claiming ages from retirement onward are compared.
  const earliest = Math.max(62, primary.ownerAge, Math.min(70, inputs.startAge));
  const ages = [...new Set([62, inputs.fullRetirementAge, 70, primary.claimAge].map((a) => clamp(a, earliest, 70)))].sort((a, b) => a - b);
  const rate = inputs.expectedReturn;
  const lifeAgeOfOwner = primary.ownerAge + inputs.yearsToRetirement + inputs.retirementYears;

  const scenarios = ages.map((claimAge) => {
    const selected = claimAge === clamp(primary.claimAge, earliest, 70);
    const scenario = selected ? ctx : makeContext(inputs, ctx.paths, ctx.tables, buildIncome(inputs, claimAge));
    const monthly = adjustBenefitForClaimingAge(primary.monthlyAtFRA, claimAge, primary.birthYear);
    const yearsPaid = Math.max(0, lifeAgeOfOwner - claimAge);
    // What the checks would be worth at the end of the plan had each one been
    // invested (or, equally, had it replaced a withdrawal that stayed invested).
    let invested = 0;
    for (let y = 0; y < yearsPaid; y++) invested = invested * (1 + rate) + monthly * 12;
    return {
      claimAge,
      monthlyBenefit: monthly,
      lifetimeBenefit: monthly * 12 * yearsPaid,
      investedValue: invested,
      sustainableSpending: findSustainableSpending('fixed', scenario, ceiling),
      selected,
    };
  });

  const pairs = [];
  for (let i = 0; i + 1 < scenarios.length; i++) {
    const a = scenarios[i];
    const b = scenarios[i + 1];
    if (b.monthlyBenefit <= a.monthlyBenefit) continue;
    pairs.push({
      early: a.claimAge,
      late: b.claimAge,
      age: claimBreakEvenAge(a.claimAge, b.claimAge, a.monthlyBenefit * 12, b.monthlyBenefit * 12, 0),
      ageWithGrowth: claimBreakEvenAge(a.claimAge, b.claimAge, a.monthlyBenefit * 12, b.monthlyBenefit * 12, rate),
    });
  }
  return { scenarios, breakEvens: pairs, growthRate: rate, lifeAge: lifeAgeOfOwner };
}

// ── The plan against the markets that actually happened ────────────────────

/**
 * Fixed spending through every stretch of US market history long enough to
 * hold the retirement: retiring in 1928, in 1929, and so on, with the plan's
 * mix of stocks and bonds, its costs, and inflation as it was. Each stretch
 * starts from the same balance (the middle of the simulated savings years).
 * Null when history has no stretch that long.
 */
export function backtestPlan(inputs, { income = buildIncome(inputs), startBalance = null } = {}) {
  const n = inputs.retirementYears;
  const count = HISTORICAL_RETURNS.length - n + 1;
  if (!(inputs.spending > 0) || count < 1) return null;
  const start = startBalance ?? savingsAtRetirement(inputs).median;
  const shareAt = (y) => (inputs.stockShares
    ? inputs.stockShares[Math.min(inputs.yearsToRetirement + y, inputs.stockShares.length - 1)]
    : RISK_PROFILES[inputs.riskProfile].stocks);
  const real = HISTORICAL_RETURNS.map(([stocks, bonds, inflation]) => {
    const prices = 1 + inflation / 100;
    return [(1 + stocks / 100) / prices - 1, (1 + bonds / 100) / prices - 1];
  });
  const returns = Array.from({ length: count }, (_, i) => Array.from({ length: n }, (_unused, y) => {
    const share = shareAt(y);
    return real[i + y][0] * share + real[i + y][1] * (1 - share) - (inputs.fee || 0);
  }));
  const paths = { start: new Float64Array(count).fill(start), returns, count };
  const ctx = makeContext(inputs, paths, strategyTables(inputs), income);
  const legacy = inputs.legacyTarget || 0;
  const record = {
    balance: new Float64Array(n), spend: new Float64Array(n), draw: new Float64Array(n),
    cash: new Float64Array(n), action: new Int8Array(n),
  };

  const env = ctx.envAt(inputs.spending);
  const plan = planFor(inputs.spending, env, n);
  const windows = returns.map((path, i) => {
    const result = runPath('fixed', plan, start, path, env, ctx.tables, n, record);
    let shortAge = null;
    for (let y = 0; y < n && shortAge == null; y++) {
      if (record.spend[y] < inputs.spending * income.factor[y] * SPENDING_FLOOR - 0.5) shortAge = inputs.startAge + y;
    }
    return {
      startYear: HISTORY_FIRST_YEAR + i,
      held: succeeded(result.lowest, inputs.spending, result.balance, legacy, result.ranOut),
      ranOut: result.ranOut,
      endBalance: result.balance,
      shortAge,
    };
  });

  // The most the plan could have spent and still held up from every start year.
  const holdsEverywhere = (level) => {
    const levelEnv = ctx.envAt(level);
    const levelPlan = planFor(level, levelEnv, n);
    return returns.every((path) => {
      const result = runPath('fixed', levelPlan, start, path, levelEnv, ctx.tables, n, null);
      return succeeded(result.lowest, level, result.balance, legacy, result.ranOut);
    });
  };
  let guaranteed = 0;
  for (let y = 0; y < n; y++) guaranteed = Math.max(guaranteed, income.pension[y] + income.ss[y]);
  let lo = 0;
  let hi = guaranteed + 0.1 * start + 1000;
  for (let i = 0; i < 12 && holdsEverywhere(hi); i++) {
    lo = hi;
    hi *= 2;
  }
  for (let i = 0; i < 15; i++) {
    const mid = (lo + hi) / 2;
    if (holdsEverywhere(mid)) lo = mid; else hi = mid;
  }

  const failed = windows.filter((w) => !w.held);
  return {
    firstYear: HISTORY_FIRST_YEAR,
    lastYear: HISTORY_FIRST_YEAR + HISTORICAL_RETURNS.length - 1,
    lastStartYear: HISTORY_FIRST_YEAR + count - 1,
    years: n,
    startBalance: start,
    count,
    held: count - failed.length,
    // Start years in which the money never ran out, the usual historical test.
    neverRanOut: windows.filter((w) => !w.ranOut).length,
    failedStartYears: failed.map((w) => w.startYear),
    worst: [...windows].sort((a, b) => a.endBalance - b.endBalance).slice(0, 3),
    medianEndBalance: percentile(Float64Array.from(windows, (w) => w.endBalance).sort(), 0.5),
    windows,
    spendingThatAlwaysHeld: lo,
  };
}

// ── What-ifs: a death, a pension paid as a lump sum, an annuity ────────────

function compareSurvivor(inputs, whatIf, asPlanned) {
  if (!inputs.married || inputs.spouseAge == null || !(inputs.spending > 0)) return null;
  const n = inputs.retirementYears;
  const y = clamp(inputs.survivorAge > 0 ? inputs.survivorAge - inputs.startAge : Math.min(10, Math.floor(n / 2)), 0, n - 1);
  return {
    age: inputs.startAge + y,
    spouseAge: inputs.spouseAge + inputs.yearsToRetirement + y,
    spendingShare: inputs.survivorSpending,
    asPlanned,
    cases: [1, 2].map((who) => ({ who, ...whatIf({ ...inputs, survivor: { who, y } }) })),
  };
}

function comparePensionChoices(inputs, whatIf, asPlanned) {
  const horizon = inputs.yearsToRetirement + inputs.retirementYears;
  return inputs.pensions.map((p, index) => {
    const offset = p.startAge - p.ownerAge;
    // A pension already being paid has no choice left to make.
    if (!(p.lumpSum > 0) || !(p.annual > 0) || offset < 0 || offset >= horizon) return null;
    const changed = {
      ...inputs,
      pensions: inputs.pensions.map((q, i) => (i === index ? { ...q, annual: 0 } : q)),
      // Rolled into a traditional account, so tax falls due as it is withdrawn.
      lumps: [...inputs.lumps, { offset, amount: p.lumpSum, account: 'traditional' }],
    };
    return { index, startAge: p.startAge, annual: p.annual, cola: p.cola, lumpSum: p.lumpSum, monthly: asPlanned, lump: whatIf(changed) };
  }).filter(Boolean);
}

function compareAnnuity(inputs, whatIf, asPlanned) {
  const a = inputs.annuity;
  if (!a || !(inputs.spending > 0)) return null;
  const income = a.amount * a.rate;
  const changed = {
    ...inputs,
    annuity: null,
    // A fixed payment for life, bought with traditional money, taxed as it is paid.
    pensions: [...inputs.pensions, { annual: income, startAge: a.age, cola: false, ownerAge: inputs.currentAge, lumpSum: 0, survivorShare: 0 }],
    lumps: [...inputs.lumps, { offset: a.age - inputs.currentAge, amount: -a.amount, account: 'traditional' }],
  };
  return { ...a, income, without: asPlanned, with: whatIf(changed) };
}

// Equity in a home, released once in the year savings would otherwise run out.
function compareHomeReserve(inputs, whatIf, asPlanned) {
  const home = inputs.home;
  if (!home || !(inputs.spending > 0)) return null;
  const amount = home.equity * home.share;
  if (!(amount > 0)) return null;
  return { ...home, amount, without: asPlanned, with: whatIf({ ...inputs, reserve: amount }) };
}

// ── The plan ───────────────────────────────────────────────────────────────

export function runRetirementPlan(inputs, { numSims = DEFAULT_NUM_SIMS, sequences = null } = {}) {
  const n = inputs.retirementYears;
  const income = buildIncome(inputs);
  const tables = strategyTables(inputs);
  const markets = sequences || simulatedMarkets(inputs, inputs.yearsToRetirement + n, numSims);
  const paths = buildPaths(inputs, numSims, markets);
  const ctx = makeContext(inputs, paths, tables, income);
  const env = ctx.envAt(inputs.spending);
  const accounts = projectAccounts(inputs, { income });

  const sortedStart = sortedCopy(paths.start);
  const startingPortfolio = {
    p10: percentile(sortedStart, 0.10),
    median: percentile(sortedStart, 0.5),
    p90: percentile(sortedStart, 0.90),
  };

  // A first guess at the most any plan could spend. It must not depend on the
  // spending target, or the search would give a different answer for it.
  let guaranteed = 0;
  for (let y = 0; y < n; y++) guaranteed = Math.max(guaranteed, income.pension[y] + income.ss[y]);
  const ceiling = guaranteed + 0.3 * startingPortfolio.p90 + 1000;
  const examples = pickExamples(paths, n, startingPortfolio.median);

  const strategies = STRATEGY_KEYS.map((key) => {
    const summary = summariseStrategy(key, inputs.spending, ctx, examples);
    const sustainableSpending = SEARCHABLE.includes(key) ? findSustainableSpending(key, ctx, ceiling) : null;
    // A flexible method earns its higher starting level by cutting later, so
    // the starting level alone flatters it. Report what living at that level
    // was actually like: the lean years, the average, and how often it cut.
    const lived = sustainableSpending != null ? summariseStrategy(key, sustainableSpending, ctx, examples) : null;
    return {
      ...summary,
      sustainableSpending,
      sustainableDraw: sustainableSpending != null ? ctx.envAt(sustainableSpending).drawFor(0, sustainableSpending) : null,
      atSustainable: lived,
    };
  });

  const fixedSustainable = strategies[0].sustainableSpending;
  const successCurve = [0.6, 0.7, 0.8, 0.9, 0.95, 1, 1.05, 1.1, 1.2, 1.3, 1.5].map((multiple) => {
    const spending = Math.max(fixedSustainable, 1000) * multiple;
    return { spending, successRate: successRate('fixed', spending, ctx) };
  });

  const firstDraw = env.drawFor(0, inputs.spending);
  const yearByYear = Array.from({ length: n }, (_, y) => {
    const draw = env.drawFor(y, inputs.spending);
    const gross = income.pension[y] + income.ss[y] + draw;
    const incomeOnly = env.spendFor(y, 0);
    const credit = accounts.rows[y]?.healthCredit ?? 0;
    return {
      age: inputs.startAge + y,
      pension: income.pension[y],
      socialSecurity: income.ss[y],
      portfolioDraw: draw,
      // What the plan spends that year on everything in the spending figure.
      spending: inputs.spending * income.factor[y] - income.parts.relief[y],
      // Income tax (federal, and state if a rate is set) plus any early-withdrawal penalty.
      federalTax: Math.max(0, gross - income.fixed[y] + credit - Math.max(inputs.spending, incomeOnly) * income.factor[y]),
      healthCost: income.parts.health[y] - credit + income.parts.medicare[y],
      otherCosts: income.parts.mortgage[y] + income.parts.care[y] + income.parts.oneTime[y] + income.parts.payroll[y],
      moneyIn: income.inflow[y] + income.inflowTraditional[y],
      afterTaxIncomeOnly: incomeOnly * income.factor[y],
    };
  });

  let lastIncomeStart = 0;
  for (let y = 1; y < n; y++) {
    if (income.pension[y] + income.ss[y] > income.pension[y - 1] + income.ss[y - 1] + 1) lastIncomeStart = y;
  }
  const steadyDraw = yearByYear[lastIncomeStart]?.portfolioDraw ?? firstDraw;
  const steadyFromAge = inputs.startAge + lastIncomeStart;

  // The projection compares choices at the expected return. A choice that
  // pays tax early can look better there and still support less spending in
  // weak markets, so each option also gets the simulation's answer.
  const supported = (changes, isPlan) => (isPlan
    ? fixedSustainable
    : findSustainableSpending('fixed', makeContext({ ...inputs, ...changes }, paths, tables, income), ceiling));
  const withdrawalOrders = compareWithdrawalOrders(inputs, accounts)
    .map((option) => ({ ...option, sustainableSpending: supported({ withdrawalOrder: option.order }, option.selected) }));
  const roth = compareRothConversions(inputs, accounts);
  [roth.baseline, ...roth.options].forEach((option) => {
    option.sustainableSpending = supported({ rothConversion: option.fillToRate }, option.selected);
  });
  const earlyAccess = compareEarlyAccess(inputs, accounts);
  if (earlyAccess) {
    earlyAccess.options.forEach((option) => {
      option.sustainableSpending = supported(option.changes, option.selected);
    });
  }

  const firstRow = accounts.rows[0];

  // The same plan with one thing different, through the same markets.
  const asPlanned = { successRate: strategies[0].successRate, sustainableSpending: fixedSustainable };
  const whatIf = (changed) => {
    const scenario = makeContext(changed, buildPaths(changed, numSims, markets), strategyTables(changed));
    return {
      successRate: successRate('fixed', inputs.spending, scenario),
      sustainableSpending: findSustainableSpending('fixed', scenario, ceiling),
    };
  };

  return {
    inputsKey: inputs.key,
    inputs,
    numSims: paths.count,
    taxYear: TAX_CONSTANTS_YEAR,
    backtest: (() => {
      const test = backtestPlan(inputs, { income, startBalance: startingPortfolio.median });
      if (!test) return null;
      // The simulated markets from the same starting balance the history test
      // uses, so the two can be compared like for like.
      const sameStart = { start: new Float64Array(paths.count).fill(startingPortfolio.median), returns: paths.returns, count: paths.count };
      return { ...test, simulatedSameStart: successRate('fixed', inputs.spending, makeContext(inputs, sameStart, tables, income)) };
    })(),
    survivor: compareSurvivor(inputs, whatIf, asPlanned),
    pensionChoices: comparePensionChoices(inputs, whatIf, asPlanned),
    annuity: compareAnnuity(inputs, whatIf, asPlanned),
    homeReserve: compareHomeReserve(inputs, whatIf, asPlanned),
    // The same plan and the same markets with every year's return a little
    // lower: what the answer owes to the return assumption (or to costs of
    // investing not entered), as distinct from the luck of the draw.
    sensitivity: {
      asPlanned,
      cases: RETURN_SENSITIVITY.map((shift) => ({ shift, ...whatIf(withReturnShift(inputs, shift)) })),
    },
    spendingSchedule: spendingSchedule(inputs, income),
    // Age of the second person when the plan ends, for the screen to show.
    spouseAgeAtEnd: inputs.spouseAge != null ? inputs.spouseAge + inputs.yearsToRetirement + inputs.retirementYears : null,
    startingPortfolio,
    target: {
      spending: inputs.spending,
      firstYearDraw: firstDraw,
      // Income tax and any early-withdrawal penalty together, and its parts,
      // all from the first row of the projection the tax tables print.
      firstYearTax: firstRow ? firstRow.federalTax + firstRow.stateTax + firstRow.penalty : 0,
      firstYearFederalTax: firstRow?.federalTax ?? 0,
      firstYearStateTax: firstRow?.stateTax ?? 0,
      firstYearPenalty: firstRow?.penalty ?? 0,
      firstYearDrawRate: startingPortfolio.median > 0 ? firstDraw / startingPortfolio.median : 0,
      // The draw once every pension and Social Security benefit is running,
      // and the age that starts. Before then the portfolio also pays the gap.
      steadyDraw,
      steadyFromAge,
      steadyDrawRate: startingPortfolio.median > 0 ? steadyDraw / startingPortfolio.median : 0,
    },
    strategies,
    successCurve,
    yearByYear,
    accounts,
    withdrawalOrders,
    roth,
    earlyAccess,
    socialSecurity: compareClaimAges(inputs, ctx, ceiling),
  };
}

// ── A baseline's own expectation ───────────────────────────────────────────

/**
 * The range of savings a plan, as it stood in `year`, simulated for `years`
 * later: the 1-in-10 low, the middle and the 1-in-10 high. While the person
 * is still saving it is the plan with retirement moved to that point; after
 * that it is the balance left by fixed spending that many years in.
 */
export function expectedSavingsAfter(profile, simulationConfig, year, years, { numSims = 600 } = {}) {
  const base = buildPlanInputs(profile, simulationConfig, year);
  if (!(years > 0)) return null;
  if (years <= base.yearsToRetirement) {
    const moved = buildPlanInputs({ ...profile, spouse1: { ...profile.spouse1, retirementAge: base.currentAge + years } }, simulationConfig, year);
    return { phase: 'saving', age: base.currentAge + years, ...savingsAtRetirement(moved) };
  }
  const into = years - base.yearsToRetirement;
  if (into > base.retirementYears || !(base.spending > 0)) return null;
  const paths = buildPaths(base, numSims, simulatedMarkets(base, base.yearsToRetirement + base.retirementYears, numSims));
  const summary = summariseStrategy('fixed', base.spending, makeContext(base, paths, strategyTables(base)));
  return {
    phase: 'retired',
    age: base.currentAge + years,
    p10: summary.p10BalanceByYear[into - 1],
    median: summary.medianBalanceByYear[into - 1],
    p90: summary.p90BalanceByYear[into - 1],
  };
}

// ── Risks beyond ordinary markets ──────────────────────────────────────────

// What each stress test assumes. They are illustrations of size, not forecasts.
export const STRESS = {
  // A fall in the first year of retirement, on top of whatever each market does afterwards.
  firstYearFall: -0.30,
  // Social Security paid at this share of scheduled benefits from this year: about
  // what the program's trustees project if its trust fund runs out and the law is not changed.
  socialSecurity: { year: 2033, factor: 0.77 },
  // Federal income tax higher across the board.
  taxFactor: 1.2,
  // Living this many years longer than the plan runs to.
  extraYears: 5,
  // A spell of care late in life, where the plan has none entered.
  care: { annual: 100000, years: 3, startAge: 85 },
};

/**
 * The plan with one bad thing added at a time, through the same simulated
 * markets: how often the entered spending still holds up, and the most each
 * case supports. Run on request (task `planRisks`); nothing here changes the
 * plan. Cases that do not apply to a plan are left out.
 */
export function stressPlan(inputs, { numSims = DEFAULT_NUM_SIMS } = {}) {
  if (!(inputs.spending > 0)) return null;
  const n = inputs.retirementYears;
  const income = buildIncome(inputs);
  const markets = simulatedMarkets(inputs, inputs.yearsToRetirement + n, numSims);
  const paths = buildPaths(inputs, numSims, markets);
  let guaranteed = 0;
  for (let y = 0; y < n; y++) guaranteed = Math.max(guaranteed, income.pension[y] + income.ss[y]);
  const ceiling = guaranteed + 0.3 * percentile(sortedCopy(paths.start), 0.90) + 1000;
  const measure = (changed, ownPaths) => {
    const ctx = makeContext(changed, ownPaths || buildPaths(changed, numSims, markets), strategyTables(changed));
    return { successRate: successRate('fixed', inputs.spending, ctx), sustainableSpending: findSustainableSpending('fixed', ctx, ceiling) };
  };
  const asPlanned = measure(inputs, paths);
  const cases = [];

  // Every market starts retirement with a large fall.
  const fallen = { ...paths, returns: paths.returns.map((path) => { const copy = Array.from(path); copy[0] = STRESS.firstYearFall; return copy; }) };
  cases.push({ id: 'firstYearFall', fall: -STRESS.firstYearFall, age: inputs.startAge, ...measure(inputs, fallen) });

  if (income.ss.some((benefit) => benefit > 0)) {
    const fromYear = Math.max(0, STRESS.socialSecurity.year - (inputs.currentYear || STRESS.socialSecurity.year));
    cases.push({ id: 'socialSecurity', year: Math.max(STRESS.socialSecurity.year, inputs.currentYear || 0), cut: 1 - STRESS.socialSecurity.factor, ...measure({ ...inputs, ssCut: { fromYear, factor: STRESS.socialSecurity.factor } }) });
  }

  cases.push({ id: 'tax', rise: STRESS.taxFactor - 1, ...measure({ ...inputs, taxFactor: STRESS.taxFactor }) });

  // Living longer needs longer markets: each path is the same market carried on.
  // Income entered as lasting to the end of the plan lasts to the new end.
  const longer = {
    ...inputs,
    lifeAge: inputs.lifeAge + STRESS.extraYears,
    retirementYears: n + STRESS.extraYears,
    otherIncome: (inputs.otherIncome || []).map((row) => (row.toAge >= inputs.lifeAge ? { ...row, toAge: row.toAge + STRESS.extraYears } : row)),
  };
  const longerPaths = buildPaths(longer, numSims, simulatedMarkets(longer, longer.yearsToRetirement + longer.retirementYears, numSims));
  cases.push({ id: 'longLife', toAge: longer.lifeAge, ...measure(longer, longerPaths) });

  if (!(inputs.care?.annual > 0 && inputs.care.years > 0)) {
    const startAge = Math.max(inputs.startAge, Math.min(STRESS.care.startAge, inputs.lifeAge - STRESS.care.years));
    if (startAge + STRESS.care.years <= inputs.lifeAge) {
      cases.push({ id: 'care', ...STRESS.care, startAge, ...measure({ ...inputs, care: { annual: STRESS.care.annual, years: STRESS.care.years, startAge } }) });
    }
  }
  return { inputsKey: inputs.key, numSims: paths.count, spending: inputs.spending, asPlanned, cases };
}

// Yearly return differences tested beside the plan.
export const RETURN_SENSITIVITY = [-0.005, -0.01];
const withReturnShift = (inputs, shift) => ({
  ...inputs,
  returnShift: (inputs.returnShift || 0) + shift,
  expectedReturn: inputs.expectedReturn + shift,
  expectedReturns: inputs.expectedReturns ? inputs.expectedReturns.map((r) => r + shift) : inputs.expectedReturns,
});

/**
 * What the plan spends each year, as the screens and the report should
 * describe it: the starting amount, each planned change with the age it
 * begins, and the costs paid on top of spending with the ages they run.
 */
export function spendingSchedule(inputs, income = buildIncome(inputs)) {
  const n = inputs.retirementYears;
  const steps = [];
  for (let y = 1; y < n; y++) {
    const planned = inputs.spending * income.factor[y] - income.parts.relief[y];
    const before = inputs.spending * income.factor[y - 1] - income.parts.relief[y - 1];
    if (Math.abs(planned - before) > 0.5) {
      steps.push({ fromAge: inputs.startAge + y, share: inputs.spending > 0 ? planned / inputs.spending : income.factor[y], amount: planned });
    }
  }
  const span = (values) => {
    let first = -1;
    let last = -1;
    values.forEach((value, y) => { if (value > 0.5) { if (first < 0) first = y; last = y; } });
    return first < 0 ? null : { fromAge: inputs.startAge + first, toAge: inputs.startAge + last, first: values[first], last: values[last] };
  };
  const extras = [
    ['health', 'Health coverage before Medicare'],
    ['medicare', 'Medicare premiums'],
    ['mortgage', 'Mortgage'],
    ['care', 'Care'],
  ].map(([id, label]) => ({ id, label, ...span(income.parts[id]) })).filter((extra) => extra.fromAge != null);
  return {
    start: inputs.spending * (income.factor[0] ?? 1) - (income.parts.relief[0] || 0),
    startAge: inputs.startAge,
    steps,
    extras,
    stepped: steps.length > 0,
  };
}

// ── Retirement-age explorer ────────────────────────────────────────────────

/**
 * How often fixed spending holds up for each combination of retirement age and
 * spending level. Every age is tested against the same simulated markets, so
 * differences between rows come from the retirement age and not from chance.
 */
export function exploreRetirementAges(profile, simulationConfig, { numSims = DEFAULT_NUM_SIMS } = {}) {
  const base = buildPlanInputs(profile, simulationConfig);
  if (!(base.spending > 0)) return { inputsKey: base.key, rows: [], levels: [] };

  const first = Math.max(base.currentAge, 50);
  const last = Math.min(75, base.lifeAge - 1);
  const step = last - first > 13 ? 2 : 1;
  const ages = [];
  for (let age = first; age <= last; age += step) ages.push(age);
  // The user's own plan is always one of the rows.
  if (!ages.includes(base.startAge) && base.startAge <= base.lifeAge - 1) {
    ages.push(base.startAge);
    ages.sort((a, b) => a - b);
  }

  const levels = [...new Set([0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3]
    .map((multiple) => Math.max(500, Math.round((base.spending * multiple) / 500) * 500)))];
  const sequences = simulatedMarkets(base, base.lifeAge - base.currentAge, numSims);

  const rows = ages.map((age) => {
    const inputs = buildPlanInputs({ ...profile, spouse1: { ...profile.spouse1, retirementAge: age } }, simulationConfig);
    const income = buildIncome(inputs);
    const paths = buildPaths(inputs, numSims, sequences);
    const ctx = makeContext(inputs, paths, strategyTables(inputs), income);
    const n = inputs.retirementYears;
    const sortedStart = sortedCopy(paths.start);
    const p90 = percentile(sortedStart, 0.90);
    let guaranteed = 0;
    for (let y = 0; y < n; y++) guaranteed = Math.max(guaranteed, income.pension[y] + income.ss[y]);
    return {
      age,
      medianPortfolio: percentile(sortedStart, 0.5),
      // The weak end of the simulated savings years: where shortfalls come from.
      lowPortfolio: percentile(sortedStart, 0.10),
      lowestPortfolio: sortedStart[0],
      sustainableSpending: findSustainableSpending('fixed', ctx, guaranteed + 0.3 * p90 + 1000),
      successByLevel: levels.map((spending) => successRate('fixed', spending, ctx)),
    };
  });

  return { inputsKey: base.key, rows, levels, targetSpending: base.spending, plannedAge: base.startAge, lifeAge: base.lifeAge, numSims };
}

// ── What moves the retirement date ─────────────────────────────────────────

/**
 * How often the entered spending holds up and the most the plan could spend,
 * from the standard set of markets and by the same search as the full plan
 * run, so Overview, the levers, Compare, the search and Spend-Down agree.
 */
export function standardHeadline(inputs, { medianInHand = false } = {}) {
  const n = inputs.retirementYears;
  const income = buildIncome(inputs);
  const tables = strategyTables(inputs);
  const paths = buildPaths(inputs, DEFAULT_NUM_SIMS, simulatedMarkets(inputs, inputs.yearsToRetirement + n, DEFAULT_NUM_SIMS));
  const ctx = makeContext(inputs, paths, tables, income);
  let guaranteed = 0;
  for (let y = 0; y < n; y++) guaranteed = Math.max(guaranteed, income.pension[y] + income.ss[y]);
  const sorted = sortedCopy(paths.start);
  const ceiling = guaranteed + 0.3 * percentile(sorted, 0.90) + 1000;
  const headline = {
    supported: findSustainableSpending('fixed', ctx, ceiling),
    ...(inputs.spending > 0 ? successAndRunway('fixed', inputs.spending, ctx) : { successRate: 1, neverRanOutRate: 1 }),
  };
  if (!medianInHand) return headline;
  // The same markets after retirement, every one starting from the middle
  // savings figure: how much of the shortfall is the retirement itself, and
  // how much the chance of arriving with less.
  const median = percentile(sorted, 0.5);
  const fixedStart = makeContext(inputs, { ...paths, start: new Float64Array(paths.count).fill(median) }, tables, income);
  return { ...headline, medianInHandRate: inputs.spending > 0 ? successRate('fixed', inputs.spending, fixedStart) : 1 };
}

const NEXT_ALLOCATION = { conservative: 'balanced', balanced: 'growth', growth: 'aggressive' };
const ALLOCATION_ORDER = ['conservative', 'balanced', 'growth', 'aggressive'];
// The order changes are made in when several are combined: pay first, so a
// saving step measured against pay uses the pay it is combined with.
const LEVER_ORDER = ['earnMore', 'fasterRaises', 'saveMore', 'spendLess', 'claimLater', 'waitFiveYears', 'moreStocks'];

/**
 * The changes the lever comparison can try, each at a few sizes (`sizes`,
 * `defaultSize`). `make(size, profile, config)` returns the change at that
 * size, measured against the profile it is applied to, which is how several
 * combine.
 */
function leverDefinitions(profile, simulationConfig, base) {
  const percentMode = profile.savingsMode === 'percent';
  const income = dollars(profile.income);
  const working = base.yearsToRetirement > 0;
  const schedule = buildSavingsPlan(profile, { currentAge: base.currentAge, retirementAge: base.startAge });
  const savingNow = schedule.length > 0 && schedule[0].total > 0;
  // Anything saved at all before retirement, the match included.
  const savesAnything = schedule.some((row) => row.total > 0);
  // Pay levers act through savings: with nothing saved, more pay changes nothing.
  const payMovesSavings = percentMode ? Number(profile.savingsRate) > 0 : dollars(profile.annualSavings) > 0;
  // Years in which pay is still set to rise faster than prices.
  const raiseYears = Math.min(base.pay.growthUntilAge, base.startAge) - base.pay.workStartAge;
  // Benefits not yet being paid, whose claiming age could still move to 70.
  const movable = base.socialSecurity.filter((s) => s.monthlyAtFRA > 0 && s.claimAge < 70 && s.ownerAge <= s.claimAge);
  const claimAges = [...new Set(movable.map((s) => s.claimAge))].sort((a, b) => a - b);
  // A dollar saving step: a twentieth of pay, or a tenth of savings for someone without pay.
  const savingStep = (p) => (dollars(p.income) > 0 ? 0.05 * dollars(p.income) : Math.max(1000, 0.1 * dollars(p.annualSavings)));
  const step = savingStep(profile);
  const money = (value) => `$${Math.round(value).toLocaleString()}`;
  const otherAllocations = ALLOCATION_ORDER.filter((name) => name !== base.riskProfile);
  const pauses = [2, 5, 10].filter((years) => years < base.yearsToRetirement);

  return [
    {
      id: 'saveMore',
      sizes: percentMode
        ? [5, 10, 15, 20].map((value) => ({ value, label: `+${value} pts` }))
        : [1, 2, 3, 4].map((value) => ({ value, label: `+${money(Math.round(value * step))}` })),
      defaultSize: percentMode ? 5 : 1,
      make: (size, p) => {
        if (percentMode) {
          const from = Number(p.savingsRate) || 0;
          const to = clamp(from + size, 0, 100);
          return { label: `Save ${size}% more of your pay`, detail: `From ${from}% to ${to}% of pay, every year.`, change: { savingsRate: to } };
        }
        const more = Math.round(size * savingStep(p));
        return { label: `Save ${money(more)} more a year`, detail: 'On top of what you save now, growing at the same rate.', change: { annualSavings: dollars(p.annualSavings) + more } };
      },
      skip: !working,
    },
    {
      id: 'earnMore',
      sizes: [10, 25, 50, 100].map((value) => ({ value, label: value === 100 ? 'x2' : `+${value}%` })),
      defaultSize: 10,
      make: (size, p) => {
        const factor = 1 + size / 100;
        return {
          label: size === 100 ? 'Earn twice as much' : `Earn ${size}% more`,
          detail: `${size === 100 ? 'Twice the pay' : `${size}% more pay`} every year, saving the same share.`,
          change: { income: dollars(p.income) * factor, ...(percentMode ? {} : { annualSavings: dollars(p.annualSavings) * factor }) },
        };
      },
      skip: !working || !(income > 0) || !payMovesSavings,
    },
    {
      id: 'fasterRaises',
      sizes: [0.5, 1, 2].map((value) => ({ value, label: `+${value} pt${value === 1 ? '' : 's'}` })),
      defaultSize: 1,
      make: (size, p) => {
        const points = size === 1 ? 'one point' : `${size} points`;
        return {
          label: `Raises ${points} a year larger`,
          detail: percentMode ? `Pay grows ${points} faster each year, and savings rise with it.` : `Pay and savings both grow ${points} faster each year.`,
          change: { incomeGrowthRate: (Number(p.incomeGrowthRate) || 0) + size, ...(percentMode ? {} : { savingsGrowthRate: (Number(p.savingsGrowthRate) || 0) + size }) },
        };
      },
      skip: !(income > 0) || raiseYears < 2 || !payMovesSavings,
    },
    {
      id: 'spendLess',
      sizes: [10, 20, 30, -10].map((value) => ({ value, label: value > 0 ? `-${value}%` : `+${-value}%` })),
      defaultSize: 10,
      make: (size, p) => ({
        label: size > 0 ? `Spend ${size}% less in retirement` : `Spend ${-size}% more in retirement`,
        detail: size > 0 ? 'A smaller budget needs less saved.' : 'A bigger budget needs more saved.',
        change: { annualSpending: dollars(p.annualSpending) * (1 - size / 100) },
      }),
      skip: !(base.spending > 0),
    },
    {
      id: 'claimLater',
      sizes: [{ value: 70, label: 'At 70' }],
      defaultSize: 70,
      make: () => ({
        label: 'Claim Social Security at 70',
        detail: `Instead of ${claimAges.join(' and ')}. A bigger check for life, with savings covering the wait.`,
        // A spouse with only a spousal amount gains nothing by waiting past full retirement age.
        change: { ss1ClaimAge: 70, ...(base.married && base.socialSecurity[1]?.monthlyAtFRA > 0 ? { ss2ClaimAge: 70 } : {}) },
      }),
      skip: movable.length === 0,
    },
    {
      id: 'waitFiveYears',
      sizes: pauses.map((value) => ({ value, label: `${value} yrs` })),
      defaultSize: pauses.includes(5) ? 5 : pauses[pauses.length - 1],
      make: (size) => ({
        label: savingNow ? `Stop saving for the next ${size} years` : `Wait ${size} years before saving`,
        detail: savingNow
          ? `Nothing added for ${size} years, then back to the plan. What a pause costs.`
          : `Nothing saved for ${size} years, then the plan as normal. What a late start costs.`,
        change: { savingsDelayYears: size },
        // A what-if, not something to write into the plan.
        illustrative: true,
      }),
      skip: pauses.length === 0 || !savesAnything,
    },
    {
      id: 'moreStocks',
      sizes: otherAllocations.map((value) => ({ value, label: value })),
      defaultSize: NEXT_ALLOCATION[base.riskProfile] || otherAllocations[otherAllocations.length - 1],
      make: (size, p, config) => ({
        label: `Hold ${ALLOCATION_ORDER.indexOf(size) > ALLOCATION_ORDER.indexOf(base.riskProfile) ? 'more' : 'less'} in stocks (${size})`,
        detail: ALLOCATION_ORDER.indexOf(size) > ALLOCATION_ORDER.indexOf(base.riskProfile)
          ? 'More growth expected, with bigger swings.'
          : 'Less growth expected, with smaller swings.',
        config: { ...config, riskProfile: size },
      }),
      skip: otherAllocations.length === 0,
    },
  ].filter((definition) => !definition.skip);
}

/** Several lever changes made together, in `LEVER_ORDER`, each measured against the plan as changed so far. */
function applyLevers(pairs, profile, simulationConfig) {
  const ordered = [...pairs].sort(([a], [b]) => LEVER_ORDER.indexOf(a.id) - LEVER_ORDER.indexOf(b.id));
  let changed = profile;
  let config = simulationConfig;
  let change = null;
  let illustrative = false;
  const made = ordered.map(([definition, size]) => {
    const step = definition.make(size, changed, config);
    if (step.change) {
      changed = { ...changed, ...step.change };
      change = { ...(change || {}), ...step.change };
    }
    if (step.config) config = step.config;
    illustrative = illustrative || !!step.illustrative;
    return step;
  });
  // Displayed in the order the screen lists the levers.
  const shown = pairs.map(([definition]) => made[ordered.findIndex(([d]) => d === definition)]);
  return {
    profile: changed,
    config,
    change,
    illustrative,
    label: shown.length === 1 ? shown[0].label : shown.map((step) => step.label).join(', '),
    detail: shown.length === 1 ? shown[0].detail : 'These changes together.',
  };
}
const LATEST_AGE_TESTED = 75;

/**
 * One change at a time to the plan, each rerun through the same simulated
 * markets, to show which choices move the retirement date and the savings at
 * retirement the most. "Earliest age" is the youngest retirement age at which
 * the plan's own spending holds up in the success target's share of markets.
 */
/**
 * Reruns a plan with something changed, through the same simulated markets
 * each time: the earliest retirement age that holds up, and at the planned
 * age the savings, how often the spending holds up and the most it could be.
 */
function planEvaluator(profile, simulationConfig, numSims, ageSims = OVERVIEW_SIMS) {
  const base = buildPlanInputs(profile, simulationConfig);
  const marketsFor = new Map();
  const markets = (inputs, count) => {
    const id = `${count}|${inputs.riskProfile}|${inputs.inflation}|${inputs.stockShares ? inputs.stockShares.join(',') : ''}`;
    if (!marketsFor.has(id)) marketsFor.set(id, simulatedMarkets(inputs, Math.max(1, 120 - inputs.currentAge), count));
    return marketsFor.get(id);
  };

  const evaluate = (changedProfile, changedConfig = simulationConfig) => {
    const at = (age) => buildPlanInputs({ ...changedProfile, spouse1: { ...changedProfile.spouse1, retirementAge: age } }, changedConfig);
    const planned = at(base.startAge);
    const contextFor = (inputs, count = numSims) => makeContext(inputs, buildPaths(inputs, count, markets(planned, count)), strategyTables(inputs));
    // Earliest ages come from the Overview's own markets whatever this run's
    // size, so Overview, the levers and the splits name the same age.
    const holdsUp = (age) => {
      const inputs = at(age);
      if (!(inputs.spending > 0)) return true;
      return successRate('fixed', inputs.spending, contextFor(inputs, ageSims), SUCCESS_TARGET) >= SUCCESS_TARGET;
    };

    // Working longer adds savings and shortens the retirement to pay for, so
    // the answer is the boundary between ages that fall short and ages that hold.
    // A plan already in retirement has no age left to choose.
    const first = planned.currentAge;
    const last = Math.max(first, Math.min(LATEST_AGE_TESTED, planned.lifeAge - 1));
    let earliestAge = null;
    if (base.yearsToRetirement > 0 && holdsUp(last)) {
      let lo = first;
      let hi = last;
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (holdsUp(mid)) hi = mid; else lo = mid + 1;
      }
      earliestAge = lo;
    }

    // At the planned age: what arrives, how often the entered spending holds
    // up, and the most the plan could spend. The last two still move for
    // someone a few years from retirement, or already in it, when a whole
    // year of retirement age is too coarse to show anything.
    const ctx = contextFor(planned);
    const start = sortedCopy(ctx.paths.start);
    let guaranteed = 0;
    for (let y = 0; y < ctx.n; y++) guaranteed = Math.max(guaranteed, ctx.income.pension[y] + ctx.income.ss[y]);
    // Reported from the standard set of markets, so this screen shows the same
    // savings figure as Spend-Down and the growth simulation.
    const arriving = savingsAtRetirement(planned);
    return {
      earliestAge,
      wealth: arriving.median,
      wealthLow: arriving.p10,
      spending: planned.spending,
      supported: findSustainableSpending('fixed', ctx, guaranteed + 0.3 * percentile(start, 0.90) + 1000, 12),
      successRate: planned.spending > 0 ? successRate('fixed', planned.spending, ctx) : 1,
    };
  };
  return { base, evaluate };
}

export function analyzeLevers(profile, simulationConfig, { numSims = 1000, baselineOnly = false, sizes = {}, only = null, combination = null } = {}) {
  const { base, evaluate } = planEvaluator(profile, simulationConfig, numSims);

  const definitions = leverDefinitions(profile, simulationConfig, base);
  const sizeOf = (definition, wanted) => (definition.sizes.some((o) => o.value === wanted) ? wanted : definition.defaultSize);
  const working = base.yearsToRetirement > 0;

  const sameMarkets = evaluate(profile);
  // The headline figures come from the standard set of markets, so every
  // screen shows the same ones; the levers are still measured against the
  // baseline run through their own, smaller set.
  const baseline = { ...sameMarkets, ...standardHeadline(base, { medianInHand: working }) };

  const row = (id, made, extra) => {
    const result = evaluate(made.profile, made.config);
    return {
      id,
      ...extra,
      label: made.label,
      detail: made.detail,
      // What "Use in my plan" writes into the plan.
      change: made.change,
      riskProfile: made.config !== simulationConfig ? made.config.riskProfile : null,
      earliestAge: result.earliestAge,
      yearsEarlier: baseline.earliestAge != null && result.earliestAge != null ? baseline.earliestAge - result.earliestAge : null,
      wealth: result.wealth,
      wealthChange: result.wealth - baseline.wealth,
      supported: result.supported,
      supportedChange: result.supported - sameMarkets.supported,
      successRate: result.successRate,
      // Room between what the plan could spend and what it is asked to: the one
      // measure every change moves, at every age.
      roomChange: (result.supported - result.spending) - (sameMarkets.supported - sameMarkets.spending),
      illustrative: made.illustrative,
    };
  };

  // The Overview page wants the headline answer alone, quickly. A screen
  // trying one lever at another size asks for that lever alone.
  const levers = (baselineOnly ? [] : definitions)
    .filter((definition) => !only || only.includes(definition.id))
    .map((definition) => {
      const size = sizeOf(definition, sizes[definition.id]);
      return row(definition.id, applyLevers([[definition, size]], profile, simulationConfig), {
        size,
        defaultSize: definition.defaultSize,
        sizes: definition.sizes,
      });
    });

  // Several changes made together, which is not the same as their rows added up.
  const picked = combination
    ? definitions.filter((definition) => combination[definition.id] != null && definition.sizes.some((o) => o.value === combination[definition.id]))
    : [];
  const combined = picked.length > 0
    ? row('combination', applyLevers(picked.map((definition) => [definition, combination[definition.id]]), profile, simulationConfig), {
      parts: picked.map((definition) => ({ id: definition.id, size: combination[definition.id] })),
    })
    : null;

  return {
    inputsKey: base.key,
    numSims,
    plannedAge: base.startAge,
    currentAge: base.currentAge,
    retired: !working,
    spending: base.spending,
    latestAgeTested: Math.max(base.currentAge, Math.min(LATEST_AGE_TESTED, base.lifeAge - 1)),
    baseline,
    levers,
    combined,
  };
}

// Markets behind the chance-by-age curve: the overview's own, so the curve
// crosses the success target at the earliest age it reports.
const OVERVIEW_SIMS = 600;

/**
 * The Overview's charts, for the plan as entered:
 *   wealth   savings by age from now to the end of the plan (1 in 10 low,
 *            middle, 1 in 10 high), through the standard markets, spending the
 *            entered amount from the planned age
 *   curve    how often that spending holds up at each retirement age
 *   sources  where each retirement year's money comes from, in the projection
 */
export function overviewCharts(profile, simulationConfig) {
  const inputs = buildPlanInputs(profile, simulationConfig);
  const n = inputs.retirementYears;
  const years = inputs.yearsToRetirement;
  const out = { inputsKey: inputs.key, plannedAge: inputs.startAge, currentAge: inputs.currentAge, spending: inputs.spending, wealth: [], curve: [], sources: [] };

  // Savings by age: the same growth as `savingsAtRetirement`, year by year.
  const all = simulatedMarkets(inputs, years + n);
  const shift = inputs.returnShift || 0;
  const lumps = lumpsBefore(inputs).total;
  const saving = Array.from({ length: years }, () => new Float64Array(all.length));
  all.forEach((path, i) => {
    let balance = inputs.accounts.total;
    for (let y = 0; y < years; y++) {
      balance = Math.max(0, balance * (1 + path[y] + shift) + inputs.savings[y] + lumps[y]);
      saving[y][i] = balance;
    }
  });
  out.wealth.push({ age: inputs.currentAge, p10: inputs.accounts.total, median: inputs.accounts.total, p90: inputs.accounts.total });
  saving.forEach((column, y) => {
    const sorted = column.sort();
    out.wealth.push({ age: inputs.currentAge + y + 1, p10: percentile(sorted, 0.10), median: percentile(sorted, 0.5), p90: percentile(sorted, 0.90) });
  });
  if (!(inputs.spending > 0) || n <= 0) return out;

  const income = buildIncome(inputs);
  const ctx = makeContext(inputs, buildPaths(inputs, DEFAULT_NUM_SIMS, all), strategyTables(inputs), income);
  const fixed = summariseStrategy('fixed', inputs.spending, ctx);
  fixed.medianBalanceByYear.forEach((median, y) => {
    out.wealth.push({ age: inputs.startAge + y + 1, p10: fixed.p10BalanceByYear[y], median, p90: fixed.p90BalanceByYear[y] });
  });

  out.sources = projectAccounts(inputs, { income }).rows.map((row) => ({
    age: row.age,
    socialSecurity: row.socialSecurity,
    otherIncome: row.pension + row.taxFreeIncome,
    // A required withdrawal larger than the year needs is taxed and put back
    // in the taxable account: shown apart, so it is not read as spending.
    fromSavings: Math.max(0, row.traditionalWithdrawal + row.rothWithdrawal + row.taxableWithdrawal - (row.surplus || 0)),
    reinvested: row.surplus || 0,
    tax: row.federalTax + (row.stateTax || 0) + row.penalty,
  }));

  if (years > 0) {
    const sequences = simulatedMarkets(inputs, Math.max(1, 120 - inputs.currentAge), OVERVIEW_SIMS);
    const last = Math.max(inputs.currentAge, Math.min(LATEST_AGE_TESTED, inputs.lifeAge - 1));
    for (let age = inputs.currentAge; age <= last; age += 1) {
      const at = buildPlanInputs({ ...profile, spouse1: { ...profile.spouse1, retirementAge: age } }, simulationConfig);
      const rate = successRate('fixed', at.spending, makeContext(at, buildPaths(at, OVERVIEW_SIMS, sequences), strategyTables(at)));
      out.curve.push({ age, rate });
    }
  }
  return out;
}

// Shares tried by the comparison of where savings go: the part of savings that
// goes to a taxable account, and how the rest divides between traditional and Roth.
const SPLIT_TAXABLE_SHARES = [0, 0.2, 0.4, 0.6, 0.8];
const SPLIT_TRADITIONAL_SHARES = [1, 0.75, 0.5, 0.25, 0];

/**
 * Yearly room for the person's own savings in today's dollars, for each
 * earner: a 401(k) (traditional or Roth) and an IRA. Pre-tax IRA money is
 * deductible only below an income limit for someone with a workplace plan;
 * the Roth IRA is open at any income through a conversion of a non-deductible
 * contribution. Health savings accounts are not counted.
 */
export function savingsRoom(row, filing, spouseAgeGap = 0) {
  const phaseout = CONTRIBUTION_LIMITS.traditionalIRADeductionPhaseout[filing] || CONTRIBUTION_LIMITS.traditionalIRADeductionPhaseout.single;
  const deductible = row.income < phaseout.end;
  // Each earner's own 401(k) and IRA room, with the catch-up amounts from 50
  // (and the larger 401(k) catch-up from 60 to 63).
  const roomAt = (age) => {
    const catchUp401k = age >= 60 && age <= 63 ? CONTRIBUTION_LIMITS.catchUp401kSuperAge60to63 : age >= 50 ? CONTRIBUTION_LIMITS.catchUp401k : 0;
    const catchUpIra = age >= 50 ? CONTRIBUTION_LIMITS.catchUpIRA : 0;
    return { plan: CONTRIBUTION_LIMITS.traditional401k + catchUp401k, ira: CONTRIBUTION_LIMITS.rothIRA + catchUpIra };
  };
  const earners = [];
  if (row.ownIncome > 0 || !(row.spouseIncome > 0)) earners.push(roomAt(row.age));
  if (row.spouseIncome > 0) earners.push(roomAt(row.age + spouseAgeGap));
  return earners.reduce((sum, e) => ({
    pretax: sum.pretax + e.plan + (deductible ? e.ira : 0),
    sheltered: sum.sheltered + e.plan + e.ira,
  }), { pretax: 0, sheltered: 0 });
}

/**
 * The plan rerun with new savings divided different ways between traditional,
 * Roth and taxable accounts, each costing the same take-home pay as the plan
 * as entered. Money put in a traditional account lowers that year's federal
 * tax on pay, so the same take-home buys more of it: each split's savings are
 * scaled until the take-home cost over the working years matches. Splits that
 * would need more room than the yearly limits allow in some year are left
 * out (the plan as entered is kept, last if it does not fit). Rows are ranked
 * by earliest retirement age, then by what the plan could spend at the planned
 * age. A search of the model, not advice.
 */
export function compareSavingsSplits(profile, simulationConfig, { numSims = 1000 } = {}) {
  const { base, evaluate } = planEvaluator(profile, simulationConfig, numSims);
  const rows = buildSavingsPlan(profile, { currentAge: base.currentAge, retirementAge: base.startAge, delayYears: profile.savingsDelayYears });
  const own = rows.reduce((sum, row) => sum + row.ownSavings, 0);
  const out = { inputsKey: base.key, numSims, plannedAge: base.startAge, currentAge: base.currentAge, latestAgeTested: Math.max(base.currentAge, Math.min(LATEST_AGE_TESTED, base.lifeAge - 1)), rows: [] };
  if (!(base.yearsToRetirement > 0) || !(own > 0) || !(base.spending > 0)) return out;

  const percentMode = profile.savingsMode === 'percent';
  const filing = base.filingStatus;
  const spouseAgeGap = base.spouseAge != null ? base.spouseAge - base.currentAge : 0;
  // The plan's own split of the person's savings: as chosen, or today's mix.
  const entered = base.savingsSplit || base.shares;
  const cost = (factor, split) => rows.reduce((sum, row) => {
    const saved = row.ownSavings * factor;
    return sum + saved - taxDeferred(row.income, filing, 0, saved * split.traditional);
  }, 0);
  const target = cost(1, entered);
  // The factor that gives the same take-home cost: cost rises with it, so halve the gap.
  const sameCost = (split) => {
    let lo = 0.2;
    let hi = 3;
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      if (cost(mid, split) < target) lo = mid; else hi = mid;
    }
    return (lo + hi) / 2;
  };
  const fits = (factor, split) => rows.every((row) => {
    const room = savingsRoom(row, filing, spouseAgeGap);
    const saved = row.ownSavings * factor;
    return saved * split.traditional <= room.pretax + 1 && saved * (split.traditional + split.roth) <= room.sheltered + 1;
  });
  const percent = (split) => ({
    traditional: Math.round(split.traditional * 100),
    roth: Math.round(split.roth * 100),
    taxable: Math.round(split.taxable * 100),
  });
  const firstSaving = rows.find((row) => row.ownSavings > 0);
  const savingsChange = (factor) => (percentMode
    ? { savingsRate: Math.round((Number(profile.savingsRate) || 0) * factor * 10) / 10 }
    : { annualSavings: Math.round(dollars(profile.annualSavings) * factor) });

  const candidates = [];
  SPLIT_TAXABLE_SHARES.forEach((taxable) => {
    SPLIT_TRADITIONAL_SHARES.forEach((trad) => {
      if (taxable === 1) return;
      candidates.push({ traditional: (1 - taxable) * trad, roth: (1 - taxable) * (1 - trad), taxable });
    });
  });

  const asEntered = evaluate(profile);
  const enteredShown = percent(entered);
  out.entered = enteredShown;
  out.chosen = !!base.savingsSplit;
  const results = [{
    split: enteredShown,
    asEntered: true,
    factor: 1,
    fitsLimits: fits(1, entered),
    firstYearSaving: firstSaving.ownSavings,
    change: null,
    ...asEntered,
  }];
  candidates.forEach((split) => {
    const shown = percent(split);
    if (shown.traditional === enteredShown.traditional && shown.roth === enteredShown.roth && shown.taxable === enteredShown.taxable) return;
    const factor = sameCost(split);
    if (!fits(factor, split)) return;
    const change = { savingsSplit: shown, ...savingsChange(factor) };
    results.push({
      split: shown,
      asEntered: false,
      factor,
      fitsLimits: true,
      firstYearSaving: firstSaving.ownSavings * factor,
      change,
      ...evaluate({ ...profile, ...change }),
    });
  });

  // A plan as entered that the yearly limits would not allow is listed after every split that they do.
  const rank = (row) => (row.earliestAge == null ? Infinity : row.earliestAge);
  results.sort((a, b) => Number(b.fitsLimits) - Number(a.fitsLimits) || rank(a) - rank(b) || b.supported - a.supported);
  results.forEach((row) => {
    row.yearsEarlier = asEntered.earliestAge != null && row.earliestAge != null ? asEntered.earliestAge - row.earliestAge : null;
    row.supportedChange = row.supported - asEntered.supported;
  });
  out.rows = results;
  out.tried = candidates.length;
  out.firstSavingAge = firstSaving.age;
  out.penaltyBefore = base.startAge < PENALTY_FREE_AGE;
  return out;
}
